import React, { useState, useMemo, useEffect } from 'react';
import {
    Button,
    Text,
    Paper,
    Badge,
    Loader,
    Alert,
    ActionIcon,
    Tooltip,
    Group,
    Title,
    Box,
    Grid,
    Tabs,
    Accordion,
    Anchor,
    Stack
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconCopy,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconChevronDown,
    IconChevronUp,
    IconAlertTriangle,
    IconInfoCircle,
    IconEye,
    IconEyeOff
} from '@tabler/icons-react';
import { ScimReferenceDisplay } from '../../common/ScimReferenceDisplay';
import { ShadcnContentViewer } from '../../common/ShadcnContentViewer';
import { HtmlPreview } from '../../common/HtmlPreview';
import { MembershipListViewer } from '../../common/MembershipListViewer';
import { ReferenceListViewer } from '../../common/ReferenceListViewer';
import { TargetTypeLink } from '../../common/TargetTypeLink';
import { TargetIdLink } from '../../common/TargetIdLink';
import { PolicyRuleIdLink } from '../../common/PolicyRuleIdLink';
import AceEditor from 'react-ace';
import 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import { GuidDisplay, isGuid } from '../../common/GuidDisplay';
import { SidDisplay } from '../../common/SidDisplay';
import { DialogErrorAlert } from '../DialogErrorAlert';
import { SqlDisplayComponent } from '../../common/SqlDisplayComponent';
import {
    entityFieldSectionHasMatch,
    getEntityFieldFilterColProps,
    countEntityFieldFilterMatches,
    type EntityFieldFilterEntry,
} from '../../../utils/entityFieldFilterUtils';
import { EntityFieldFilterCountReporter } from '../../common/EntityFieldFilterCountReporter';
import {
    categorizeEntityProperties,
    buildEntityForSummaryDisplay,
    buildCategorizedPropsFromTier3ReadableAttrs,
    CategorizedProperties,
    shouldRenderAsGuid,
    shouldRenderAsSid,
    isIsoDateTime,
    shouldRenderAsDateTime,
    NON_INDEXED_PROPERTY_STUB,
    isARef
} from '../../../utils/entityPropertyUtils';
import { formatWorkflowConfigForDisplay, isJsonFieldReturnedAsString, JSON_FIELD_STRING_WARNING_MESSAGE } from '../../../utils/workflowConfigUtils';
import {
    isSet,
    isEmailTemplate,
    isWorkflow,
    isSetTransition,
    isContent,
    isFile
} from '../../../utils/entityTypeDetection';
import {
    ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD,
    EAGER_LOAD_NON_INDEXED_MAX_LENGTH,
    formatTier3AttributeSizeEstimate,
    getReadableAttributeIdsFromTargetResponse,
    getReadableAttributesWithTypesFromTargetResponse,
    getReadableButNoLengthCountAttributeIds,
    getNonIndexedOrNoLengthCountAttributeRefsFromTier2,
    getBindingConstraintsFromTargetEntitlements,
    getBindingConstraintsSummary,
    getBindingViolationMessage,
    getTargetEntitlementsList,
    getTargetEntitlementItemForAttribute,
    getTier2AttributeBindingForAttribute,
    tier3AttributeEncrypted,
    tier3ItemDecryptAllowed,
    formatTargetEntitlementItemForDisplay,
    type EntityEntitlementForTargetResponse,
    type BindingConstraints
} from '../../../types/EntityEntitlements';
import { isA, createEntityRef, EntityRef } from '../../../types';
import EntitlementsService from '../../../services/EntitlementsService';
import { formatDateTime } from '../../../utils/dateTimeUtils';
import { copyToClipboard } from '../../../utils/clipboardUtils';
import { detectFieldType } from '../../../utils/fieldTypeDetection';
import { debugService } from '../../../services/DebugService';
import { debugLog, debugVerboseLog, stringifyDebugNotes } from '../../../utils/debugLog';
import { getAxiosInstance } from '../../../services/axiosInstance';
import { getScimConfig } from '../../../utils/envUtils';
import { PaginatedEntityViewer } from '../../common/PaginatedEntityViewer';
import {
    base64ToBytes,
    bytesToBlobPart,
    getBestFileContentType,
    getBestFileFilename,
    getWhitespaceIssues,
    hasWhitespaceIssues,
    inferMimeTypeFromFilename,
    isExpectedMultilineField,
} from './entityDetailValueUtils';

export interface FilterPreviewOpenConfig {
    filter: string;
    scope: string;
    titleOverride?: string;
    noteMessage?: string;
    attributes?: string[];
}

export interface EntityDetailPropertiesRendererProps {
    entity: any;
    entityType: string;
    fullEntity: any;
    targetEntitlement: EntityEntitlementForTargetResponse | null;
    schemaForSummary: any;
    schemaAttributes: any[];
    entityService: any;
    possessiveTargetAttributeInfo: any;
    actionWorkflowError: string | null;
    actionWorkflowValidating: boolean;
    isAdmin: boolean;
    readableEntityIdsLower: Set<string>;
    colorScheme: string;
    fieldFilterQuery: string;
    handleFieldFilterCountsChange: (counts: { matching: number; total: number }) => void;
    handleReferenceClick: (entity: any) => void;
    loadNonIndexedAttribute: (key: string) => Promise<any>;
    loadingAttribute: string | null;
    openContentBodyFullScreen: () => void;
    showFieldDebugInfo: boolean;
    contentBodyMode: string | null;
    copyDeepLinkWithBodyLoaded: () => void;
    onOpenFilterPreview: (config: FilterPreviewOpenConfig) => void;
}

export const EntityDetailPropertiesRenderer: React.FC<EntityDetailPropertiesRendererProps> = ({
    entity: entityProp,
    entityType,
    fullEntity,
    targetEntitlement,
    schemaForSummary,
    schemaAttributes,
    entityService,
    possessiveTargetAttributeInfo,
    actionWorkflowError,
    actionWorkflowValidating,
    isAdmin,
    readableEntityIdsLower,
    colorScheme,
    fieldFilterQuery,
    handleFieldFilterCountsChange,
    handleReferenceClick,
    loadNonIndexedAttribute,
    loadingAttribute,
    openContentBodyFullScreen,
    showFieldDebugInfo,
    onOpenFilterPreview,
}) => {
    const [configExpanded, setConfigExpanded] = useState<'collapsed' | 'normal' | 'expanded'>('normal');
    const [scriptExpanded, setScriptExpanded] = useState<'collapsed' | 'normal' | 'expanded'>('normal');
    const [jsonExpanded, setJsonExpanded] = useState<'collapsed' | 'normal' | 'expanded'>('normal');
    const [emailCopied, setEmailCopied] = useState(false);
    const [idCopied, setIdCopied] = useState(false);
    const [configCopied, setConfigCopied] = useState(false);
    const [scriptCopied, setScriptCopied] = useState(false);
    const [decryptReveal, setDecryptReveal] = useState<{ fieldLabel: string; attributePath: string; value: string | null; loading: boolean; error: string | null } | null>(null);
    const [revealedDecryptedCache, setRevealedDecryptedCache] = useState<Record<string, string>>({});
    const [encryptedFieldVisibleByPath, setEncryptedFieldVisibleByPath] = useState<Record<string, boolean>>({});
    const [foldedNonIndexedFields, setFoldedNonIndexedFields] = useState<Record<string, boolean>>({});
    const [htmlBodyTabByField, setHtmlBodyTabByField] = useState<Record<string, number>>({});

    const fileDownloadInFlightRef = React.useRef(false);
    const [isDownloadingFileData, setIsDownloadingFileData] = useState(false);
    const [isFileDataFallbackReady, setIsFileDataFallbackReady] = useState(false);
    const fileDataFallbackTimerRef = React.useRef<number | null>(null);
    const fileDataPrimaryUsedRef = React.useRef(false);

    useEffect(() => {
        setConfigExpanded('normal');
        setScriptExpanded('normal');
        setJsonExpanded('normal');
        setEmailCopied(false);
        setIdCopied(false);
        setConfigCopied(false);
        setScriptCopied(false);
        setDecryptReveal(null);
        setRevealedDecryptedCache({});
        setEncryptedFieldVisibleByPath({});
        setFoldedNonIndexedFields({});
        setHtmlBodyTabByField({});
        fileDownloadInFlightRef.current = false;
        fileDataPrimaryUsedRef.current = false;
        setIsDownloadingFileData(false);
        setIsFileDataFallbackReady(false);
        if (fileDataFallbackTimerRef.current != null) {
            window.clearTimeout(fileDataFallbackTimerRef.current);
            fileDataFallbackTimerRef.current = null;
        }
    }, [entityProp?.Id, entityType]);
    const bindingConstraintsMap = React.useMemo(
        () => getBindingConstraintsFromTargetEntitlements(targetEntitlement ?? null),
        [targetEntitlement]
    );

    /** Render field label (displayName) with optional tooltip for bindings. Lookup by attr.id, display attr.displayName. */
    const renderFieldLabelWithBindingTooltip = (attr: EntityRef, options?: { gutterBottom?: boolean; suffix?: string | number }) => {
        const constraints = bindingConstraintsMap[(attr.id || '').toLowerCase()] ?? bindingConstraintsMap[attr.id];
        const summary = constraints ? getBindingConstraintsSummary(constraints) : '';
        const text = options?.suffix != null ? `${attr.displayName} (${options.suffix})` : attr.displayName;
        const label = (
            <Text size="sm" c="dimmed" mb={options?.gutterBottom ? 4 : 0}>
                {text}
            </Text>
        );
        if (summary) return <Tooltip label={summary}>{label}</Tooltip>;
        return label;
    };

    /** Best-effort renderer name for a field based on tier3 metadata (mirrors edit dialog semantics for debug only). */
    const getFieldRendererName = (attr: EntityRef): string | null => {
        const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
        const tAttr = tier3Item?.Attribute;
        if (!tAttr) return null;
        const type = (tAttr.Type ?? '').toString().toLowerCase();
        const isArray = tAttr.Array === true;
        const isRef = tAttr.IsReference === true;
        const isLargeArrayRef = isArray && isRef && (
            (typeof tAttr.Count === 'number' && tAttr.Count > ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD) ||
            (typeof tAttr.Length === 'number' && tAttr.Length > EAGER_LOAD_NON_INDEXED_MAX_LENGTH)
        );

        // In view dialog, large array refs are rendered as a "View" button (paginated viewer), not as chips.
        if (isLargeArrayRef) return 'PaginatedEntityViewer';
        if (isArray && isRef) return 'ReferenceFieldMulti';
        if (isRef) return 'ReferenceField';

        if (type === 'json' || type === 'object' || type === 'complex') {
            return 'JsonEditor';
        }

        // Heuristics for SQL / script fields (match edit dialog behavior closely enough for debug purposes)
        const idLower = (attr.id ?? '').toString().toLowerCase();
        if (idLower.includes('sql') || idLower.includes('query')) {
            return 'SqlEditor';
        }
        if (idLower.includes('script')) {
            return 'ScriptEditor';
        }

        return 'DefaultText';
    };

    /** Tier3 entitlement tooltip content for a field (when showFieldDebugInfo is on). Uses attr.id for lookup. */
    const getFieldTier3TooltipContent = (attr: EntityRef): React.ReactNode => {
        const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
        if (!tier3Item) return null;
        const rendererName = getFieldRendererName(attr);
        const lines: string[] = [];
        if (rendererName) {
            lines.push(`Renderer: ${rendererName}`);
            lines.push(''); // blank line before entitlement block
        }
        lines.push(formatTargetEntitlementItemForDisplay(tier3Item));

        return (
            <Box
                component="span"
                display="block"
                ff="monospace"
                fz="xs"
                style={{
                    whiteSpace: 'pre-wrap',
                    maxWidth: 320,
                }}
            >
                {lines.join('\n')}
            </Box>
        );
    };

    /** Label (displayName) + optional tier3 info icon. Uses attr.id for tier3/binding lookup, attr.displayName for label. */
    const renderFieldLabelWithTier3Debug = (attr: EntityRef, options?: { gutterBottom?: boolean; suffix?: string | number }) => {
        const tier3Item = showFieldDebugInfo ? getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id) : null;
        return (
            <Group component="span" gap="xs" align="center" wrap="nowrap">
                {renderFieldLabelWithBindingTooltip(attr, options)}
                {showFieldDebugInfo && tier3Item && (
                    <Tooltip label={getFieldTier3TooltipContent(attr)} position="left">
                        <IconInfoCircle size={18} />
                    </Tooltip>
                )}
            </Group>
        );
    };

    // shouldRenderAsGuid, shouldRenderAsSid, isIsoDateTime, shouldRenderAsDateTime are now imported from entityPropertyUtils
    // formatDateTime is now imported from dateTimeUtils

    // isExpectedMultilineField/getWhitespaceIssues/hasWhitespaceIssues moved to entityDetailValueUtils.ts

    const renderFileDataDownloadActions = (fieldKey: string, dataValue: unknown): React.ReactNode | null => {
        const normalizedKey = (fieldKey ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
        if (normalizedKey !== 'data') return null;
        if (!isFile(entityType)) return null;

        // IMPORTANT: Filename/content-type should come from the already-loaded entity,
        // not from the "load just one attribute" response which may only include Data.
        const metadataEntity = fullEntity ?? entityProp;
        const filename = getBestFileFilename(metadataEntity);
        const mimeType = getBestFileContentType(metadataEntity) ?? inferMimeTypeFromFilename(filename);

        const downloadBlob = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        };

        const runFallback = async () => {
            // Old fallback: use the attribute-load API call, then client-side decode.
            const loaded = await loadNonIndexedAttribute(fieldKey);
            const candidate =
                (loaded as any)?.Data ?? (loaded as any)?.data ??
                (loaded as any)?.['$.Data'] ?? (loaded as any)?.['$.data'];
            if (typeof candidate === 'string' && candidate.trim()) {
                const bytes = base64ToBytes(candidate.trim());
                downloadBlob(new Blob([bytesToBlobPart(bytes)], { type: mimeType }));
                return;
            }
            // Last resort: use the already-loaded dataValue.
            if (typeof dataValue === 'string' && dataValue.trim() && dataValue !== NON_INDEXED_PROPERTY_STUB) {
                const bytes = base64ToBytes(dataValue.trim());
                downloadBlob(new Blob([bytesToBlobPart(bytes)], { type: mimeType }));
            }
        };

        const handleDownload = async () => {
            // Ref-based lock so rapid multi-clicks can't slip in before state applies.
            if (fileDownloadInFlightRef.current || fileDataPrimaryUsedRef.current) return;
            fileDownloadInFlightRef.current = true;
            fileDataPrimaryUsedRef.current = true; // one-shot primary download
            setIsDownloadingFileData(true);
            setIsFileDataFallbackReady(false);

            if (fileDataFallbackTimerRef.current != null) {
                window.clearTimeout(fileDataFallbackTimerRef.current);
            }

            try {
                const fileId = metadataEntity?.Id ?? metadataEntity?.id;
                const serverDownloadPromise = (async () => {
                    if (!fileId) throw new Error('Missing file id');
                    const axios = getAxiosInstance();
                    const resp = await axios.get(`/entities/files/${encodeURIComponent(String(fileId))}/$download`, {
                        responseType: 'blob',
                    });
                    return resp.data as Blob;
                })();

                // After a couple seconds, show a fallback action the user can click.
                fileDataFallbackTimerRef.current = window.setTimeout(() => {
                    setIsFileDataFallbackReady(true);
                }, 2000);

                const blob = await serverDownloadPromise;
                if (fileDataFallbackTimerRef.current != null) {
                    window.clearTimeout(fileDataFallbackTimerRef.current);
                    fileDataFallbackTimerRef.current = null;
                }
                // If fallback became visible while waiting, hide it on success.
                setIsFileDataFallbackReady(false);
                downloadBlob(blob);
            } catch {
                if (fileDataFallbackTimerRef.current != null) {
                    window.clearTimeout(fileDataFallbackTimerRef.current);
                    fileDataFallbackTimerRef.current = null;
                }
                setIsFileDataFallbackReady(true);
            } finally {
                setIsDownloadingFileData(false);
                fileDownloadInFlightRef.current = false;
            }
        };

        return (
            <Group gap="sm" wrap="wrap" align="center">
                {fileDataPrimaryUsedRef.current ? (
                    <Text size="sm" c="dimmed">
                        Downloaded
                    </Text>
                ) : (
                    <Anchor
                        component="button"
                        size="sm"
                        onClick={() => {
                            if (fileDownloadInFlightRef.current || isDownloadingFileData) return;
                            void handleDownload();
                        }}
                        aria-disabled={isDownloadingFileData}
                        style={{
                            cursor: isDownloadingFileData ? 'not-allowed' : 'pointer',
                            opacity: isDownloadingFileData ? 0.7 : 1,
                            pointerEvents: isDownloadingFileData ? 'none' : 'auto',
                        }}
                    >
                        Download
                    </Anchor>
                )}
                {isFileDataFallbackReady && (
                    <Anchor
                        component="button"
                        size="sm"
                        c="dimmed"
                        onClick={() => void runFallback()}
                    >
                        Fallback download
                    </Anchor>
                )}
                {(isDownloadingFileData || isFileDataFallbackReady) && (
                    <Text size="xs" c="dimmed">
                        {isFileDataFallbackReady ? 'Fallback ready' : 'Downloadingâ€¦'}
                    </Text>
                )}
            </Group>
        );
    };

    const renderNonIndexedNotLoadedField = (key: string, noLengthCountAttributeIds?: string[]): React.ReactNode => {
        const lenEst = formatTier3AttributeSizeEstimate(key, targetEntitlement ?? null);
        const hasNoLengthCount = Array.isArray(noLengthCountAttributeIds) && noLengthCountAttributeIds.length > 0 &&
            noLengthCountAttributeIds.some((id) => (id ?? '').toLowerCase() === (key ?? '').toLowerCase());
        if (hasNoLengthCount) {
            // No length/count means no value; don't show "Not loaded*"
            return (
                <Group gap={4} wrap="wrap" align="center">
                    <Tooltip label="No value">
                        <Text component="span" c="dimmed">
                            â€”
                        </Text>
                    </Tooltip>
                </Group>
            );
        }

        // File.Data: show Download immediately (server $download with fallback),
        // instead of "Not loaded* ... Load" which would fetch into client memory first.
        const normalizedKey = (key ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
        const isFileDataField = isFile(entityType) && normalizedKey === 'data';
        if (isFileDataField) {
            return renderFileDataDownloadActions(key, '');
        }

        const loading = loadingAttribute === key;
        return (
            <Group gap={4} wrap="wrap" align="center">
                <Tooltip label="Sensitive, Large, or File value">
                    <Text component="span" style={{ wordBreak: 'break-word' }}>
                        Not loaded*{lenEst ? ` (${lenEst})` : ''}
                    </Text>
                </Tooltip>
                <Anchor
                    component="button"
                    size="sm"
                    c="dimmed"
                    onClick={() => loadNonIndexedAttribute(key)}
                    style={{ cursor: loading ? 'wait' : 'pointer' }}
                >
                    {loading ? (
                        <Group component="span" gap="xs" align="center" wrap="nowrap" style={{ display: 'inline-flex' }}>
                            <Loader size={12} /> Load
                        </Group>
                    ) : (
                        'Load'
                    )}
                </Anchor>
            </Group>
        );
    };

    const getNonIndexedFoldStateKey = (section: 'basic' | 'system', key: string): string =>
        `${section}:${(key ?? '').toLowerCase()}`;

    const isLargeLoadedNonIndexedField = (key: string, value: unknown): boolean => {
        if (value === NON_INDEXED_PROPERTY_STUB || value === undefined) return false;
        const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, key);
        const attr = tier3Item?.Attribute;
        if (!attr || attr.Indexed !== false) return false;

        if (typeof attr.Length === 'number' && attr.Length >= 449) return true;
        if (typeof attr.Count === 'number' && attr.Count >= 20) return true;

        if (typeof value === 'string') return value.length >= 449;
        if (Array.isArray(value)) return value.length >= 20;
        if (value && typeof value === 'object') {
            try {
                return JSON.stringify(value).length >= 449;
            } catch {
                return true;
            }
        }
        return false;
    };

    const isNonIndexedFieldFolded = (section: 'basic' | 'system', key: string): boolean =>
        !!foldedNonIndexedFields[getNonIndexedFoldStateKey(section, key)];

    const toggleNonIndexedFieldFold = (section: 'basic' | 'system', key: string): void => {
        const foldKey = getNonIndexedFoldStateKey(section, key);
        setFoldedNonIndexedFields((prev) => ({ ...prev, [foldKey]: !prev[foldKey] }));
    };

    // Helper function to safely render field values
    const renderFieldValue = (value: any): React.ReactNode => {
        if (value === null || value === undefined) return 'Not specified';
        if (typeof value === 'string') {
            // Check if it's an ISO 8601 date/time string
            if (isIsoDateTime(value)) {
                const formatted = formatDateTime(value);
                return (
                    <Tooltip label={value}>
                        <Text component="span">
                            {formatted}
                        </Text>
                    </Tooltip>
                );
            }
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (Array.isArray(value)) {
            return (
                <>
                    {value.map((v, idx) => (
                        <Badge key={idx} variant="light" size="sm" mr={4} mb={4}>{typeof v === 'object' ? (v.DisplayName || v.Id || JSON.stringify(v)) : String(v)}</Badge>
                    ))}
                </>
            );
        }
        if (typeof value === 'object') {
            // If it looks like a SCIM reference, render via reference component
            if (value.$ref || value.Id || value.DisplayName) {
                return renderReferenceField(value);
            }
            // Fallback stringify
            return JSON.stringify(value);
        }
        return String(value);
    };

    // handleReferenceClick is now provided by useEntityDetailLoader hook

    // Helper function to render reference fields that might be objects
    const renderReferenceField = (value: any) => {
        return (
            <ScimReferenceDisplay
                reference={value}
                variant="default"
                showType={true}
                showLink={true}
                onReferenceClick={handleReferenceClick}
            />
        );
    };

    // Helper function to render array of references
    const renderReferenceArray = (values: any[], label: string) => {
        if (!values?.length) return null;

        return (
            <Grid.Col span={12}>
                <Text size="sm" c="dimmed">{label}</Text>
                <Group gap="sm" wrap="wrap">
                    {values.map((value, index) => (
                        <ScimReferenceDisplay
                            key={index}
                            reference={value}
                            variant="compact"
                            showType={true}
                        />
                    ))}
                </Group>
                <Box mt="xs" p="xs" bg="var(--mantine-color-yellow-1)" c="var(--mantine-color-yellow-9)" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                    <Text size="xs" c="var(--mantine-color-yellow-9)">
                        Debug: {values.length} {label.toLowerCase()}(s) found
                    </Text>
                </Box>
            </Grid.Col>
        );
    };

    // Helper function to render array of strings as chips
    const renderStringArray = (values: string[], label: string) => {
        if (!values?.length) return null;

        return (
            <Grid.Col span={12}>
                <Text size="sm" c="dimmed">{label}</Text>
                <Group gap="sm" wrap="wrap">
                    {values.map((value, index) => (
                        <Badge key={index} variant="light" size="sm">{value}</Badge>
                    ))}
                </Group>
            </Grid.Col>
        );
    };

    // Helper function to render boolean field as chip
    const renderBooleanField = (value: boolean, label: string) => {
        return (
            <Grid.Col span={4}>
                <Text size="sm" c="dimmed">{label}</Text>
                <Badge
                    size="sm"
                    color={value ? 'green' : 'gray'}
                >
                    {value ? 'Yes' : 'No'}
                </Badge>
            </Grid.Col>
        );
    };




    // Helper function to categorize and render entity properties dynamically
    // Note: isJsonString is now in fieldTypeDetection utility

    const renderEntityProperties = (entity: any) => {
        if (!entity) return null;

        // Extract Query from $debug into _query property (if Debug mode is enabled)
        const processedEntity = { ...entity };
        if (debugService.isEnabled() && (processedEntity.$debug || processedEntity.$Debug)) {
            const debugObj = processedEntity.$debug || processedEntity.$Debug;
            if (debugObj && typeof debugObj === 'object' && debugObj !== null) {
                // Check if Query property exists (could be at top level or nested in Debug.Debug.Query)
                let queryValue = debugObj.Query || debugObj.query || debugObj.Sql || debugObj.sql;

                // Also check nested Debug.Debug.Query structure
                if (!queryValue && debugObj.Debug && typeof debugObj.Debug === 'object') {
                    queryValue = debugObj.Debug.Query || debugObj.Debug.query || debugObj.Debug.Sql || debugObj.Debug.sql;
                }
                // Also check nested debug.Query structure (lowercase)
                if (!queryValue && debugObj.debug && typeof debugObj.debug === 'object') {
                    queryValue = debugObj.debug.Query || debugObj.debug.query || debugObj.debug.Sql || debugObj.debug.sql;
                }

                if (queryValue) {
                    // Extract Query to _query property
                    processedEntity._query = queryValue;

                    // Remove Query from $debug (create a copy without it)
                    // Handle nested Debug.Debug structure
                    if (debugObj.Debug && typeof debugObj.Debug === 'object' && (debugObj.Debug.Query || debugObj.Debug.query || debugObj.Debug.Sql || debugObj.Debug.sql)) {
                        const { Query, query, Sql, sql, ...debugWithoutQuery } = debugObj.Debug;
                        const updatedDebugObj = { ...debugObj, Debug: debugWithoutQuery };
                        if (processedEntity.$debug) {
                            processedEntity.$debug = updatedDebugObj;
                        } else if (processedEntity.$Debug) {
                            processedEntity.$Debug = updatedDebugObj;
                        }
                    } else {
                        // Query is at top level
                        const { Query, query, Sql, sql, ...debugWithoutQuery } = debugObj;
                        if (processedEntity.$debug) {
                            processedEntity.$debug = debugWithoutQuery;
                        } else if (processedEntity.$Debug) {
                            processedEntity.$Debug = debugWithoutQuery;
                        }
                    }
                }
            }
        }

        // When tier3 is present, build the summary from entitlements first (readable attrs + types), then fill from entity.
        // This shows all readable fields including booleans with false when the API omits them.
        const readableAttrsWithTypes = getReadableAttributesWithTypesFromTargetResponse(targetEntitlement ?? null);
        let categorizedProps: CategorizedProperties;
        if (readableAttrsWithTypes != null && readableAttrsWithTypes.length > 0) {
            categorizedProps = buildCategorizedPropsFromTier3ReadableAttrs(readableAttrsWithTypes, processedEntity, entityType);
            // When the entity detail response has more data than tier3 allows, still show those extra fields
            const existingIds = new Set<string>();
            for (const entries of [categorizedProps.basic, categorizedProps.system, categorizedProps.booleans, categorizedProps.references, categorizedProps.arrays, categorizedProps.memberships] as [EntityRef, unknown][][]) {
                for (const [attr] of entries) {
                    const id = (attr.id ?? '').trim().toLowerCase();
                    if (id) existingIds.add(id);
                }
            }
            const entityForSummary = buildEntityForSummaryDisplay(processedEntity, entityType, schemaForSummary, { excludeMetaOnSummary: true });
            const fullCategorized = categorizeEntityProperties(entityForSummary, entityType);
            const categories = ['basic', 'system', 'booleans', 'references', 'arrays', 'memberships'] as const;
            for (const cat of categories) {
                for (const entry of fullCategorized[cat]) {
                    const id = (entry[0].id ?? '').trim().toLowerCase();
                    if (id && !existingIds.has(id)) {
                        existingIds.add(id);
                        categorizedProps[cat].push(entry);
                    }
                }
            }
        } else {
            // No tier3 or no restriction: additive display from entity keys, then filter by tier3 readable if present.
            // Use readable (not displayable) so attributes like Managers with HasLengthCount false still show when present.
            const entityForSummary = buildEntityForSummaryDisplay(processedEntity, entityType, schemaForSummary, { excludeMetaOnSummary: true });
            categorizedProps = categorizeEntityProperties(entityForSummary, entityType);
            const allowedReadableIds = getReadableAttributeIdsFromTargetResponse(targetEntitlement ?? null);
            if (allowedReadableIds !== undefined && allowedReadableIds.length > 0) {
                const allowedSet = new Set(allowedReadableIds.map((id) => id.toLowerCase()));
                const allowKey = (key: string) => {
                    const k = (key ?? '').trim();
                    if (!k) return false;
                    if (allowedSet.size === 0) return k.toLowerCase() === 'id' || k.toLowerCase() === 'displayname';
                    return allowedSet.has(k.toLowerCase());
                };
                const filterCategory = (entries: [EntityRef, unknown][]) => entries.filter(([attr]) => allowKey(attr.id));
                categorizedProps = {
                    basic: filterCategory(categorizedProps.basic),
                    system: filterCategory(categorizedProps.system),
                    booleans: filterCategory(categorizedProps.booleans),
                    references: filterCategory(categorizedProps.references),
                    arrays: filterCategory(categorizedProps.arrays),
                    memberships: filterCategory(categorizedProps.memberships)
                };
            }
        }

        // When tier3 failed (fallback), inject delay-load stub rows for non-indexed attributes from tier2 (e.g. Operation)
        if ((readableAttrsWithTypes == null || readableAttrsWithTypes.length === 0) && entityType) {
            const tier2ForEntity = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(entityType);
            const tier2DelayLoadRefs = getNonIndexedOrNoLengthCountAttributeRefsFromTier2(tier2ForEntity);
            const existingKeys = new Set<string>();
            for (const entries of [categorizedProps.basic, categorizedProps.system, categorizedProps.booleans, categorizedProps.references, categorizedProps.arrays, categorizedProps.memberships] as [EntityRef, unknown][][]) {
                for (const [attr] of entries) existingKeys.add((attr.id ?? '').toLowerCase());
            }
            for (const ref of tier2DelayLoadRefs) {
                if (!ref.id || existingKeys.has(ref.id.toLowerCase())) continue;
                categorizedProps.system.push([{ id: ref.id, displayName: ref.displayName }, NON_INDEXED_PROPERTY_STUB]);
                existingKeys.add(ref.id.toLowerCase());
            }
        }

        // API inconsistency: HasLengthCount is false but entity has a value for that attribute â€” treat as error from API
        // When tier3 failed we use [] so tier2-injected stubs (e.g. Operation) show "Not loaded*" + Load instead of "â€”".
        const noLengthCountIds = targetEntitlement != null
            ? getReadableButNoLengthCountAttributeIds(targetEntitlement)
            : [];
        const apiInconsistencyAttrs: string[] = [];
        if (noLengthCountIds.length > 0 && entity) {
            const getEntityVal = (e: any, attrId: string) => {
                const lower = (attrId ?? '').toLowerCase();
                const key = Object.keys(e || {}).find((k) => (k ?? '').toLowerCase() === lower);
                return key != null ? e[key] : undefined;
            };
            const hasValue = (val: unknown) => {
                if (val === undefined || val === null) return false;
                if (typeof val === 'string' && val.trim() === '') return false;
                if (Array.isArray(val) && val.length === 0) return false;
                return true;
            };
            for (const id of noLengthCountIds) {
                if (hasValue(getEntityVal(processedEntity, id))) apiInconsistencyAttrs.push(id);
            }
        }

        // Calculate total properties from categorized results
        const totalProperties = categorizedProps.basic.length + categorizedProps.system.length +
            categorizedProps.booleans.length + categorizedProps.references.length +
            categorizedProps.arrays.length + categorizedProps.memberships.length;

        // Console log summary of property categorization
        debugLog('ðŸ“Š EntityDetailDialog: Property Categorization Summary', {
            entityType,
            totalProperties,
            categories: {
                basic: categorizedProps.basic.length,
                system: categorizedProps.system.length,
                booleans: categorizedProps.booleans.length,
                references: categorizedProps.references.length,
                arrays: categorizedProps.arrays.length,
                memberships: categorizedProps.memberships.length
            },
            basicProperties: categorizedProps.basic.map(([attr]) => attr.id),
            systemProperties: categorizedProps.system.map(([attr]) => attr.id),
            booleanProperties: categorizedProps.booleans.map(([attr]) => attr.id),
            referenceProperties: categorizedProps.references.map(([attr]) => attr.id),
            arrayProperties: categorizedProps.arrays.map(([attr]) => attr.id),
            membershipProperties: categorizedProps.memberships.map(([attr]) => attr.id)
        });

        // Sort basic properties: DisplayName first, then Id, then Sequence, UserPrincipalName, Subject, Body, then rest
        categorizedProps.basic.sort(([attrA], [attrB]) => {
            if (isA(attrA, 'DisplayName')) return -1;
            if (isA(attrB, 'DisplayName')) return 1;
            if (isA(attrA, 'Id')) return -1;
            if (isA(attrB, 'Id')) return 1;
            if (isA(attrA, 'Sequence')) return -1;
            if (isA(attrB, 'Sequence')) return 1;
            if (isA(attrA, 'UserPrincipalName')) return -1;
            if (isA(attrB, 'UserPrincipalName')) return 1;
            if (isWorkflow(entityType)) {
                if (isA(attrA, 'Type')) return -1;
                if (isA(attrB, 'Type')) return 1;
            }
            const aIsSubject = isA(attrA, 'Subject');
            const bIsSubject = isA(attrB, 'Subject');
            const aIsBody = isA(attrA, 'Body');
            const bIsBody = isA(attrB, 'Body');
            if (aIsSubject && bIsBody) return -1;
            if (aIsBody && bIsSubject) return 1;
            if (aIsSubject) return -1;
            if (bIsSubject) return 1;
            if (aIsBody) return -1;
            if (bIsBody) return 1;
            return (attrA.displayName || attrA.id).localeCompare(attrB.displayName || attrB.id);
        });

        // Helper: get binding violation message for a field value (view warnings for AllowedValues/RegEx). Use attr.id for lookup.
        const getBindingViolationForField = (attr: EntityRef, val: unknown): string | null => {
            const fieldKey = attr.id;
            const constraints: BindingConstraints | undefined = bindingConstraintsMap[(fieldKey || '').toLowerCase()] ?? bindingConstraintsMap[fieldKey];
            if (!constraints) return null;
            // Skip validation warnings for values that are intentionally not loaded yet.
            if (val === NON_INDEXED_PROPERTY_STUB) return null;
            if (Array.isArray(val)) {
                for (const item of val) {
                    if (item === NON_INDEXED_PROPERTY_STUB) continue;
                    const toCheck = typeof item === 'object' && item != null && ('Id' in item || 'DisplayName' in item)
                        ? (item as { Id?: string; DisplayName?: string }).Id ?? (item as { Id?: string; DisplayName?: string }).DisplayName
                        : item;
                    const msg = getBindingViolationMessage(toCheck, constraints);
                    if (msg) return msg;
                }
                return null;
            }
            if (typeof val === 'object' && val != null && ('Id' in val || 'DisplayName' in val)) {
                return getBindingViolationMessage((val as { Id?: string; DisplayName?: string }).Id ?? (val as { Id?: string; DisplayName?: string }).DisplayName ?? '', constraints);
            }
            return getBindingViolationMessage(val, constraints);
        };

        // When tier3 response exists but grants no readable attributes (TargetEntitlements: []) or very few, show limited-access message.
        // "Few" = fewer than 4: Type, Id, and DisplayName are typically always viewable; < 4 means at most those (bare minimum).
        const hasTier3Response = targetEntitlement != null;
        const noReadableAttrs = targetEntitlement != null && getTargetEntitlementsList(targetEntitlement).length === 0;
        const fewReadableAttrs = readableAttrsWithTypes != null && readableAttrsWithTypes.length > 0 && totalProperties < 4;
        const isLimitedAccess = hasTier3Response && (noReadableAttrs || fewReadableAttrs);

        const tier2EntitlementForDetailView = entityType
            ? EntitlementsService.getCachedEntityEntitlementForSchemaEntity(entityType)
            : null;

        const tier3HasLengthCountTrue = (tier3Item: ReturnType<typeof getTargetEntitlementItemForAttribute>) => {
            if (!tier3Item?.Attribute) return true;
            const a = tier3Item.Attribute as { HasLengthCount?: boolean; hasLengthCount?: boolean };
            return a.HasLengthCount !== false && a.hasLengthCount !== false;
        };

        const getEncryptedViewSemantics = (attr: EntityRef) => {
            const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
            const tAttr = tier3Item?.Attribute ?? null;
            const isEncryptedTier3 = tier3AttributeEncrypted(tAttr);
            const tier2Item = getTier2AttributeBindingForAttribute(tier2EntitlementForDetailView, attr.id);
            const isEncryptedTier2 =
                tier2Item?.attribute?.Encrypted === true ||
                (tier2Item?.attribute as { encrypted?: boolean } | undefined)?.encrypted === true;
            const isEncrypted = isEncryptedTier3 || isEncryptedTier2;
            const rawId = (tAttr?.Id ?? attr.id ?? '').toString().trim();
            const attributePathForKey =
                rawId.startsWith('$.')
                    ? rawId
                    : (rawId ? `$.${rawId}` : (attr.id.startsWith('$.') ? attr.id : `$.${attr.id}`));
            const hasLengthCount = tier3HasLengthCountTrue(tier3Item);
            const decryptApiAllowed = tier3Item != null && tier3ItemDecryptAllowed(tier3Item);
            return { isEncrypted, isEncryptedTier3, attributePathForKey, hasLengthCount, decryptApiAllowed };
        };

        const fetchDecryptedPlaintext = (
            path: string,
            entityIdForEncdec: string,
            cached: string | undefined
        ): Promise<string> => {
            if (cached !== undefined) return Promise.resolve(cached);
            const api = getAxiosInstance(getScimConfig().apiUrl);
            const encPath = `mgmt/encdec/${encodeURIComponent(entityIdForEncdec)}/${path}`;
            return api.get(encPath).then((res) => {
                const data = res.data;
                const raw = data?.Value ?? data?.value;
                return raw !== undefined && raw !== null
                    ? (typeof raw === 'string' ? raw : JSON.stringify(raw))
                    : '';
            });
        };

        /** Encrypted / secret summary fields: always masked until "Show stored value"; decrypt is copy-only (no plaintext display). */
        const renderEncryptedSummaryField = (attr: EntityRef, cellValue: unknown): React.ReactNode => {
            const sem = getEncryptedViewSemantics(attr);
            if (!sem.isEncrypted) return null;

            const { attributePathForKey, hasLengthCount, decryptApiAllowed, isEncryptedTier3 } = sem;
            const entityIdForEncdec = entity?.Id ?? entity?.id;
            const path = attributePathForKey;
            const showTier3DecryptButton = !!isEncryptedTier3 && !!entityIdForEncdec;

            if (!hasLengthCount) {
                return (
                    <Grid.Col span={6} key={attr.id} {...filterColProps(attr, cellValue)}>
                        <Group gap="xs" align="center" mb="xs">
                            {renderFieldLabelWithTier3Debug(attr)}
                        </Group>
                        <Tooltip label="No value">
                            <Text size="sm" c="dimmed">â€”</Text>
                        </Tooltip>
                    </Grid.Col>
                );
            }

            const storedVisible = encryptedFieldVisibleByPath[path] === true;
            const cached = revealedDecryptedCache[path];
            const isThisPath = decryptReveal?.attributePath === path;
            const isCopyDecryptLoading = isThisPath && !!decryptReveal?.loading;
            const isCopyDecryptError = isThisPath && !!decryptReveal?.error;

            const storedText =
                cellValue !== undefined && cellValue !== null
                    ? typeof cellValue === 'string'
                        ? cellValue
                        : JSON.stringify(cellValue)
                    : '';

            const handleDecryptAndCopy = () => {
                if (!entityIdForEncdec || !decryptApiAllowed) return;
                setDecryptReveal({ fieldLabel: attr.displayName, attributePath: path, value: null, loading: true, error: null });
                fetchDecryptedPlaintext(path, entityIdForEncdec, cached)
                    .then(async (str) => {
                        setRevealedDecryptedCache((prev) => ({ ...prev, [path]: str }));
                        setDecryptReveal((prev) => (prev ? { ...prev, value: str, loading: false, error: null } : null));
                        const ok = await copyToClipboard(str);
                        if (ok) {
                            notifications.show({
                                title: 'Copied',
                                message: 'Decrypted value copied to clipboard.',
                                color: 'green',
                            });
                        } else {
                            notifications.show({
                                title: 'Copy failed',
                                message: 'Could not copy to clipboard.',
                                color: 'red',
                            });
                        }
                    })
                    .catch((err: unknown) => {
                        const msg =
                            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                            (err as Error)?.message ??
                            'Failed to decrypt';
                        setDecryptReveal((prev) => (prev ? { ...prev, value: null, loading: false, error: String(msg) } : null));
                    });
            };

            return (
                <Grid.Col span={6} key={attr.id} {...filterColProps(attr, cellValue)}>
                    <Group gap="xs" align="center" mb="xs">
                        {renderFieldLabelWithTier3Debug(attr)}
                    </Group>
                    <Stack gap="xs" py={4}>
                        {!storedVisible ? (
                            <Group gap="sm" wrap="wrap" align="center">
                                <Text size="sm" ff="monospace">â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢</Text>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEncryptedFieldVisibleByPath((prev) => ({ ...prev, [path]: true }))}
                                    leftSection={<IconEye size={16} />}
                                >
                                    Show stored value
                                </Button>
                            </Group>
                        ) : (
                            <>
                                <Text size="xs" c="dimmed">Stored value (encrypted payload)</Text>
                                <Text size="sm" ff="monospace" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {storedText || 'â€”'}
                                </Text>
                                <Group gap="sm" wrap="wrap">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setEncryptedFieldVisibleByPath((prev) => ({ ...prev, [path]: false }));
                                            if (decryptReveal?.attributePath === path) setDecryptReveal(null);
                                        }}
                                        leftSection={<IconEyeOff size={16} />}
                                    >
                                        Hide
                                    </Button>
                                    {showTier3DecryptButton ? (
                                        <Tooltip
                                            label="You do not have permission to decrypt this attribute."
                                            disabled={decryptApiAllowed}
                                        >
                                            <span style={{ display: 'inline-block' }}>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={handleDecryptAndCopy}
                                                    disabled={!decryptApiAllowed || isCopyDecryptLoading}
                                                    leftSection={isCopyDecryptLoading ? <Loader size={16} /> : <IconCopy size={16} />}
                                                >
                                                    Decrypt and copy
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </Group>
                                {decryptApiAllowed && showTier3DecryptButton && isCopyDecryptLoading && (
                                    <Group gap="xs" align="center">
                                        <Loader size={16} />
                                        <Text size="xs" c="dimmed">Decryptingâ€¦</Text>
                                    </Group>
                                )}
                                {decryptApiAllowed && showTier3DecryptButton && isCopyDecryptError && (
                                    <DialogErrorAlert error={decryptReveal?.error ?? null} />
                                )}
                            </>
                        )}
                    </Stack>
                </Grid.Col>
            );
        };

        const isSummaryAttrRequired = (attr: EntityRef): boolean => {
            const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
            if (tier3Item?.Binding?.Required === true) return true;
            const idNorm = (attr.id ?? '').trim().toLowerCase().replace(/^\$\./, '');
            const fromSchema = schemaAttributes?.find((a) => {
                const n = (a.name ?? a.id ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
                return n === idNorm;
            });
            return fromSchema?.required === true || fromSchema?.Required === true;
        };

        const filterColProps = (attr: EntityRef, value: unknown) =>
            getEntityFieldFilterColProps(
                (attr.displayName || attr.id || '').toString(),
                value,
                fieldFilterQuery,
                [(attr.id ?? '').toString()].filter(Boolean),
                { required: isSummaryAttrRequired(attr) }
            );
        const sectionVisible = (entries: [EntityRef, unknown][]) =>
            entityFieldSectionHasMatch(entries, fieldFilterQuery);

        const allSummaryFieldEntries: EntityFieldFilterEntry[] = (
            [
                ...categorizedProps.basic,
                ...categorizedProps.references,
                ...categorizedProps.arrays,
                ...categorizedProps.booleans,
                ...categorizedProps.memberships,
                ...categorizedProps.system,
            ] as [EntityRef, unknown][]
        ).map(([attr, value]): EntityFieldFilterEntry => [
            { ...attr, required: isSummaryAttrRequired(attr) },
            value,
        ]);
        const summaryFieldFilterCounts = countEntityFieldFilterMatches(
            allSummaryFieldEntries,
            fieldFilterQuery
        );

        return (
            <>
                <EntityFieldFilterCountReporter
                    matching={summaryFieldFilterCounts.matching}
                    total={summaryFieldFilterCounts.total}
                    onCountsChange={handleFieldFilterCountsChange}
                />
                {apiInconsistencyAttrs.length > 0 && (
                    <Grid.Col span={12}>
                        <Alert color="yellow" mb="md">
                            API inconsistency: the server reported no count/length (HasLengthCount is false) for the following attributes, but the entity has a value: {apiInconsistencyAttrs.join(', ')}. This may indicate an API or entitlement error.
                        </Alert>
                    </Grid.Col>
                )}
                {isLimitedAccess && (
                    <Grid.Col span={12}>
                        <Alert color="blue" mb="md">
                            {noReadableAttrs
                                ? 'You have limited access to this record. You do not have permission to read any attributes.'
                                : `You have limited access to this record. Only the attributes you have permission to read are shown (${totalProperties} field${totalProperties !== 1 ? 's' : ''}).`}
                        </Alert>
                    </Grid.Col>
                )}
                {/* Basic Information */}
                {sectionVisible(categorizedProps.basic) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="basic">
                            <Accordion.Item value="basic">
                                <Accordion.Control>
                                    <Title order={5}>
                                        Basic Information ({categorizedProps.basic.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="md">
                                            {categorizedProps.basic.map(([attr, value]) => {
                                                const key = attr.id;
                                                const keyNorm = (key ?? '').trim().toLowerCase().replace(/^\$\./, '');
                                                const encSummary = renderEncryptedSummaryField(attr, value);
                                                if (encSummary) return encSummary;

                                                // Use centralized field type detection
                                                const fieldInfo = detectFieldType(key, value, entityType, fullEntity, 'basic');

                                                // Extract commonly used flags
                                                const {
                                                    isEmailField,
                                                    isIdField,
                                                    isSequenceField,
                                                    isIdOrSequenceField,
                                                    isWorkflowConfig,
                                                    isScriptField: isScriptFieldInBasic,
                                                    isSqlField: isSqlFieldInBasic,
                                                    isJsonField: detectedIsJsonField,
                                                    isJsonStringField,
                                                    isJsonObject,
                                                    isOperationField,
                                                    isContentBodyMarkdown,
                                                    isContentBodyHtml,
                                                    isBodyField,
                                                    isClickableFilter
                                                } = fieldInfo;

                                                // Force Modification field to render as JSON (Ace viewer), never as a GUID/reference widget.
                                                const isForcedModificationJsonField = isARef(createEntityRef(key ?? ''), 'modification');
                                                const isJsonField = detectedIsJsonField || isForcedModificationJsonField;

                                                // Check if Id is a GUID (GuidDisplay will have its own copy button)
                                                const isIdGuid = isIdField && isGuid(value);

                                                // Debug logging for Operations/Operation field
                                                if (isOperationField || fieldInfo.isOperationsField) {
                                                    debugVerboseLog('[EntityDetailDialog] Operations field detected in basic section:', {
                                                        key,
                                                        valueType: typeof value,
                                                        valueLength: typeof value === 'string' ? value.length : 'N/A',
                                                        valuePreview: typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100),
                                                        isJsonStringCheck: isJsonStringField,
                                                        isJsonStringField,
                                                        isJsonObject,
                                                        isJsonField,
                                                        isString: typeof value === 'string',
                                                        isObject: typeof value === 'object',
                                                        entityType
                                                    });
                                                }

                                                // When SqlStatement/SqlStatementExplicit is empty or whitespace-only, show a note instead of the SQL viewer
                                                const isSqlValueEmpty = isSqlFieldInBasic && (value == null || (typeof value === 'string' && value.trim() === ''));
                                                const canFoldLargeLoadedNonIndexed = isLargeLoadedNonIndexedField(key, value);
                                                const isLargeNonIndexedFolded = canFoldLargeLoadedNonIndexed && isNonIndexedFieldFolded('basic', key);

                                                // Determine renderer for basic fields
                                                const basicRenderer = isWorkflowConfig ? 'AceEditor-json' : isJsonField ? 'AceEditor-json' : isScriptFieldInBasic ? 'AceEditor-javascript' : isSqlFieldInBasic ? 'AceEditor-sql' : isBodyField || isContentBodyHtml ? 'AceEditor-html' : isContentBodyMarkdown ? 'MarkdownPreview' : (value && typeof value === 'object' && (value.$ref || value.Id || value.DisplayName) && !isForcedModificationJsonField) ? 'ReferenceField' : 'Typography';

                                                const isTargetIdField = keyNorm === 'targetid';
                                                const entityTargetType = entity?.TargetType ?? entity?.targetType;
                                                return (
                                                    <Grid.Col
                                                        span={isBodyField || isContentBodyMarkdown || isContentBodyHtml || isWorkflowConfig || isJsonField || isScriptFieldInBasic || isSqlFieldInBasic ? 12 : 6}
                                                        key={attr.id}
                                                        {...filterColProps(attr, value)}
                                                        {...(isTargetIdField ? { 'data-target-id-field': true, 'data-target-id-value': value, 'data-target-type-value': entityTargetType } : {})}
                                                    >
                                                        {(isWorkflowConfig || isJsonField) && typeof value === 'string' && value !== NON_INDEXED_PROPERTY_STUB && isJsonFieldReturnedAsString(value, key) && (
                                                            <Alert color="yellow" mb="xs">{JSON_FIELD_STRING_WARNING_MESSAGE}</Alert>
                                                        )}
                                                        <Group gap={4} align="center" mb="xs">
                                                            <span
                                                                data-notes={stringifyDebugNotes({
                                                                    field: key,
                                                                    fieldLower: key?.toLowerCase(),
                                                                    entityType,
                                                                    entityTypeLower: entityType?.toLowerCase(),
                                                                    isWorkflowConfig,
                                                                    isScriptFieldInBasic,
                                                                    isSqlFieldInBasic,
                                                                    isBodyField,
                                                                    valueType: typeof value,
                                                                    hasValue: value !== undefined && value !== null,
                                                                    valueLength: typeof value === 'string' ? value.length : Array.isArray(value) ? value.length : 'N/A',
                                                                    renderer: basicRenderer
                                                                })}
                                                                data-field={key}
                                                                data-entity-type={entityType}
                                                                data-renderer={basicRenderer}
                                                                style={{ display: 'inline-block' }}
                                                            >
                                                                <Group gap="xs" align="center">
                                                                    {renderFieldLabelWithTier3Debug(attr)}
                                                                    {isContent(entityType) && isARef(createEntityRef(key ?? ''), 'Body') && value !== NON_INDEXED_PROPERTY_STUB && value !== undefined && (
                                                                        <Tooltip label="Full screen" withArrow>
                                                                            <ActionIcon
                                                                                variant="subtle"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    void openContentBodyFullScreen();
                                                                                }}
                                                                            >
                                                                                <IconArrowsMaximize size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    )}
                                                                    {typeof value === 'string' && hasWhitespaceIssues(value) && !isExpectedMultilineField(key) && (
                                                                        <Tooltip label={`Warning: ${getWhitespaceIssues(value).join(', ')}`}>
                                                                            <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                        </Tooltip>
                                                                    )}
                                                                    {isEmailTemplate(entityType) && key && isARef(createEntityRef(key), 'ContentType') && typeof value === 'string' && value.toLowerCase().trim() !== 'text/html' && value.toLowerCase().trim() !== 'html' && value.trim() !== '' && (
                                                                        <Tooltip label="Warning: Email templates should use 'text/html' as ContentType. Other values may cause rendering issues.">
                                                                            <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                        </Tooltip>
                                                                    )}
                                                                    {(() => {
                                                                        const bindingViolation = getBindingViolationForField(attr, value);
                                                                        return bindingViolation ? (
                                                                            <Tooltip label={bindingViolation}>
                                                                                <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                            </Tooltip>
                                                                        ) : null;
                                                                    })()}
                                                                </Group>
                                                            </span>
                                                            {(isWorkflowConfig || isJsonField || isScriptFieldInBasic || isEmailField || (isIdOrSequenceField && !isIdGuid)) && (
                                                                <>
                                                                    <Tooltip label={
                                                                        (isWorkflowConfig && configCopied) || (isScriptFieldInBasic && scriptCopied) || (isEmailField && emailCopied) || (isIdOrSequenceField && idCopied)
                                                                            ? "Copied!" : `Copy ${attr.displayName} to clipboard`
                                                                    }>
                                                                        <ActionIcon variant="subtle"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                if (isEmailField || isIdOrSequenceField) {
                                                                                    copyToClipboard(String(value));
                                                                                    if (isEmailField) {
                                                                                        setEmailCopied(true);
                                                                                        setTimeout(() => setEmailCopied(false), 2000);
                                                                                    } else if (isIdOrSequenceField) {
                                                                                        setIdCopied(true);
                                                                                        setTimeout(() => setIdCopied(false), 2000);
                                                                                    }
                                                                                } else {
                                                                                    const textToCopy = typeof value === 'string'
                                                                                        ? (isWorkflowConfig ? formatWorkflowConfigForDisplay(value) : (isJsonStringField ? (() => {
                                                                                            try {
                                                                                                return JSON.stringify(JSON.parse(value), null, 2);
                                                                                            } catch {
                                                                                                return value;
                                                                                            }
                                                                                        })() : value))
                                                                                        : (isJsonObject ? JSON.stringify(value, null, 2) : JSON.stringify(isWorkflowConfig ? formatWorkflowConfigForDisplay(value) : value, null, 2));
                                                                                    copyToClipboard(textToCopy);
                                                                                    if (isWorkflowConfig || isJsonField) {
                                                                                        setConfigCopied(true);
                                                                                        setTimeout(() => setConfigCopied(false), 2000);
                                                                                    } else if (isScriptFieldInBasic) {
                                                                                        setScriptCopied(true);
                                                                                        setTimeout(() => setScriptCopied(false), 2000);
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            <IconCopy size={16} color={
                                                                                (isWorkflowConfig && configCopied) || (isJsonField && configCopied) || (isScriptFieldInBasic && scriptCopied) || (isEmailField && emailCopied) || (isIdOrSequenceField && idCopied)
                                                                                    ? "var(--mantine-color-green-6)" : undefined
                                                                            } />
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                    {(isWorkflowConfig || isJsonField || isScriptFieldInBasic) && (
                                                                        <Tooltip label={
                                                                            (isWorkflowConfig ? configExpanded : isJsonField ? jsonExpanded : scriptExpanded) === 'expanded' ? 'Collapse to normal' :
                                                                                (isWorkflowConfig ? configExpanded : isJsonField ? jsonExpanded : scriptExpanded) === 'normal' ? 'Minimize to single line' :
                                                                                    'Expand to full size'
                                                                        }>
                                                                            <ActionIcon variant="subtle"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    // Cycle through: collapsed -> normal -> expanded -> collapsed
                                                                                    if (isWorkflowConfig) {
                                                                                        if (configExpanded === 'collapsed') {
                                                                                            setConfigExpanded('normal');
                                                                                        } else if (configExpanded === 'normal') {
                                                                                            setConfigExpanded('expanded');
                                                                                        } else {
                                                                                            setConfigExpanded('collapsed');
                                                                                        }
                                                                                    } else if (isJsonField) {
                                                                                        if (jsonExpanded === 'collapsed') {
                                                                                            setJsonExpanded('normal');
                                                                                        } else if (jsonExpanded === 'normal') {
                                                                                            setJsonExpanded('expanded');
                                                                                        } else {
                                                                                            setJsonExpanded('collapsed');
                                                                                        }
                                                                                    } else if (isScriptFieldInBasic) {
                                                                                        if (scriptExpanded === 'collapsed') {
                                                                                            setScriptExpanded('normal');
                                                                                        } else if (scriptExpanded === 'normal') {
                                                                                            setScriptExpanded('expanded');
                                                                                        } else {
                                                                                            setScriptExpanded('collapsed');
                                                                                        }
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {(isWorkflowConfig ? configExpanded : isJsonField ? jsonExpanded : scriptExpanded) === 'expanded' ? (
                                                                                    <IconArrowsMinimize size={16} />
                                                                                ) : (isWorkflowConfig ? configExpanded : isJsonField ? jsonExpanded : scriptExpanded) === 'normal' ? (
                                                                                    <IconArrowsMaximize size={16} />
                                                                                ) : (
                                                                                    <IconArrowsMaximize size={16} />
                                                                                )}
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}
                                                            {canFoldLargeLoadedNonIndexed && (
                                                                <Tooltip label={isLargeNonIndexedFolded ? 'Unfold' : 'Fold'}>
                                                                    <ActionIcon variant="subtle"
                                                                        size="sm"
                                                                        onClick={() => toggleNonIndexedFieldFold('basic', key)}
                                                                        aria-label={isLargeNonIndexedFolded ? 'Unfold' : 'Fold'}
                                                                    >
                                                                        {isLargeNonIndexedFolded ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </Group>
                                                        <Box
                                                            py="xs"
                                                            style={{ display: canFoldLargeLoadedNonIndexed && isLargeNonIndexedFolded ? 'none' : 'block' }}
                                                        >
                                                            {value === NON_INDEXED_PROPERTY_STUB ? (
                                                                renderNonIndexedNotLoadedField(key, noLengthCountIds)
                                                            ) : renderFileDataDownloadActions(key, value) ? (
                                                                renderFileDataDownloadActions(key, value)
                                                            ) : value && typeof value === 'object' && (value.$ref || value.Id || value.DisplayName) && !isForcedModificationJsonField && !isJsonField ? (
                                                                <Box>
                                                                    {renderReferenceField(value)}
                                                                    {/* Show related entities for PossessiveTargetAttribute */}
                                                                    {(key && isARef(createEntityRef(key), 'PossessiveTargetAttribute')) && possessiveTargetAttributeInfo && (
                                                                        <>
                                                                            {possessiveTargetAttributeInfo.entities.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Used by: {possessiveTargetAttributeInfo.entities.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                            {possessiveTargetAttributeInfo.isReferenceTo && possessiveTargetAttributeInfo.isReferenceTo.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Refs: {possessiveTargetAttributeInfo.isReferenceTo.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            ) : isWorkflowConfig ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    {configExpanded === 'collapsed' ? (
                                                                        <Box
                                                                            h={24}
                                                                            px="xs"
                                                                            py="xs"
                                                                            bg="var(--mantine-color-default-hover)"
                                                                            c="dimmed"
                                                                            ff="monospace"
                                                                            fz="xs"
                                                                            style={{
                                                                                overflow: 'hidden',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}
                                                                        >
                                                                            <Text
                                                                                size="xs"
                                                                                ff="monospace"
                                                                                fz="xs"
                                                                                style={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                {typeof value === 'string' ? formatWorkflowConfigForDisplay(value).substring(0, 100) : JSON.stringify(formatWorkflowConfigForDisplay(value), null, 2).substring(0, 100)}...
                                                                            </Text>
                                                                        </Box>
                                                                    ) : (
                                                                        <AceEditor
                                                                            mode="json"
                                                                            theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                            value={formatWorkflowConfigForDisplay(value)}
                                                                            readOnly={true}
                                                                            name={`config-viewer-${key}`}
                                                                            editorProps={{ $blockScrolling: true }}
                                                                            width="100%"
                                                                            height={configExpanded === 'expanded' ? '600px' : '400px'}
                                                                            fontSize={14}
                                                                            showPrintMargin={false}
                                                                            showGutter={true}
                                                                            highlightActiveLine={false}
                                                                            setOptions={{
                                                                                showLineNumbers: true,
                                                                                tabSize: 2,
                                                                                useWorker: false,
                                                                                foldStyle: 'markbeginend',
                                                                                showFoldWidgets: true
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Paper>
                                                            ) : isJsonField ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    {/* Debug logging for Operations field rendering in basic section */}
                                                                    {(() => {
                                                                        if (key && (isARef(createEntityRef(key), 'Operations') || isARef(createEntityRef(key), 'Operation'))) {
                                                                            debugVerboseLog('[EntityDetailDialog] Rendering Operations as JSON in basic section:', {
                                                                                key,
                                                                                isJsonStringField,
                                                                                isJsonObject,
                                                                                isJsonField,
                                                                                jsonExpanded,
                                                                                valueType: typeof value,
                                                                                valuePreview: typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100)
                                                                            });
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                    {jsonExpanded === 'collapsed' ? (
                                                                        <Box
                                                                            h={24}
                                                                            px="xs"
                                                                            py="xs"
                                                                            bg="var(--mantine-color-default-hover)"
                                                                            c="dimmed"
                                                                            ff="monospace"
                                                                            fz="xs"
                                                                            style={{
                                                                                overflow: 'hidden',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}
                                                                        >
                                                                            <Text
                                                                                size="xs"
                                                                                ff="monospace"
                                                                                fz="xs"
                                                                                style={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                {typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100)}...
                                                                            </Text>
                                                                        </Box>
                                                                    ) : (
                                                                        <AceEditor
                                                                            mode="json"
                                                                            theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                            value={typeof value === 'string' ? (() => {
                                                                                try {
                                                                                    return JSON.stringify(JSON.parse(value), null, 2);
                                                                                } catch {
                                                                                    return value;
                                                                                }
                                                                            })() : JSON.stringify(value, null, 2)}
                                                                            readOnly={true}
                                                                            name={`json-viewer-${key}`}
                                                                            editorProps={{ $blockScrolling: true }}
                                                                            width="100%"
                                                                            height={jsonExpanded === 'expanded' ? '600px' : '400px'}
                                                                            fontSize={14}
                                                                            showPrintMargin={false}
                                                                            showGutter={true}
                                                                            highlightActiveLine={false}
                                                                            setOptions={{
                                                                                showLineNumbers: true,
                                                                                tabSize: 2,
                                                                                useWorker: false,
                                                                                foldStyle: 'markbeginend',
                                                                                showFoldWidgets: true
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Paper>
                                                            ) : isScriptFieldInBasic ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    {scriptExpanded === 'collapsed' ? (
                                                                        <Box
                                                                            h={24}
                                                                            px="xs"
                                                                            py="xs"
                                                                            bg="var(--mantine-color-default-hover)"
                                                                            c="dimmed"
                                                                            ff="monospace"
                                                                            fz="xs"
                                                                            style={{
                                                                                overflow: 'hidden',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}
                                                                        >
                                                                            <Text
                                                                                size="xs"
                                                                                ff="monospace"
                                                                                fz="xs"
                                                                                style={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                {String(value || '').substring(0, 100)}...
                                                                            </Text>
                                                                        </Box>
                                                                    ) : (
                                                                        <AceEditor
                                                                            mode="javascript"
                                                                            theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                            value={String(value || '')}
                                                                            readOnly={true}
                                                                            name={`script-viewer-${key}`}
                                                                            editorProps={{ $blockScrolling: true }}
                                                                            width="100%"
                                                                            height={scriptExpanded === 'expanded' ? '600px' : '400px'}
                                                                            fontSize={14}
                                                                            showPrintMargin={false}
                                                                            showGutter={true}
                                                                            highlightActiveLine={false}
                                                                            setOptions={{
                                                                                showLineNumbers: true,
                                                                                tabSize: 2,
                                                                                useWorker: false,
                                                                                showFoldWidgets: true
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Paper>
                                                            ) : isSqlFieldInBasic ? (
                                                                <SqlDisplayComponent value={value} displayName={attr.displayName} editorId={key} />
                                                            ) : isClickableFilter ? (
                                                                <Text
                                                                    style={{
                                                                        fontFamily: 'monospace',
                                                                        wordBreak: 'break-word',
                                                                        color: 'primary.main',
                                                                        cursor: 'pointer',
                                                                        textDecoration: 'underline',
                                                                        '&:hover': {
                                                                            color: 'primary.dark'
                                                                        }
                                                                    }}
                                                                    onClick={() => {
                                                                        const filterScopeValue = fullEntity.FilterScope || fullEntity.filterScope || '';
                                                                        // Ensure filterScope is a string (handle case where it might be an object)
                                                                        const filterScopeString = typeof filterScopeValue === 'string'
                                                                            ? filterScopeValue
                                                                            : (filterScopeValue?.Id || filterScopeValue?.id || filterScopeValue?.DisplayName || filterScopeValue?.displayName || '');
                                                                        onOpenFilterPreview({ filter: typeof value === 'string' ? value : String(value), scope: filterScopeString });
                                                                    }}
                                                                >
                                                                    {value}
                                                                </Text>
                                                            ) : isContentBodyMarkdown ? (
                                                                <Box mt="xs">
                                                                    <ShadcnContentViewer content={value || ''} emptyMessage="No content to display" height="400px" />
                                                                </Box>
                                                            ) : isContentBodyHtml ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    <Tabs
                                                                        value={String(Math.min(htmlBodyTabByField[`basic:${key}`] ?? 0, 2))}
                                                                        onChange={(v) => v != null && setHtmlBodyTabByField((prev) => ({ ...prev, [`basic:${key}`]: Number(v) }))}
                                                                    >
                                                                        <Tabs.List>
                                                                            <Tabs.Tab value="0">Source</Tabs.Tab>
                                                                            <Tabs.Tab value="1">Preview</Tabs.Tab>
                                                                            <Tabs.Tab value="2">Side by side</Tabs.Tab>
                                                                        </Tabs.List>
                                                                        <Tabs.Panel value="0" pt="xs">
                                                                            <AceEditor
                                                                                mode="html"
                                                                                theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                                value={value || ''}
                                                                                readOnly={true}
                                                                                name={`html-viewer-${key}`}
                                                                                editorProps={{ $blockScrolling: true }}
                                                                                width="100%"
                                                                                height="400px"
                                                                                fontSize={14}
                                                                                showPrintMargin={false}
                                                                                showGutter={true}
                                                                                highlightActiveLine={false}
                                                                                setOptions={{
                                                                                    showLineNumbers: true,
                                                                                    tabSize: 2,
                                                                                    useWorker: false
                                                                                }}
                                                                            />
                                                                        </Tabs.Panel>
                                                                        <Tabs.Panel value="1" pt="xs">
                                                                            <HtmlPreview content={typeof value === 'string' ? value : ''} emptyMessage="No content to preview" height="400px" />
                                                                        </Tabs.Panel>
                                                                        <Tabs.Panel value="2" pt="xs">
                                                                            <Box style={{ display: 'flex', height: '400px' }}>
                                                                                <Box style={{ flex: 1, borderRight: 1, borderColor: 'var(--mantine-color-default-border)' }}>
                                                                                    <AceEditor
                                                                                        mode="html"
                                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                                        value={value || ''}
                                                                                        readOnly={true}
                                                                                        name={`html-viewer-side-${key}`}
                                                                                        editorProps={{ $blockScrolling: true }}
                                                                                        width="100%"
                                                                                        height="400px"
                                                                                        fontSize={14}
                                                                                        showPrintMargin={false}
                                                                                        showGutter={true}
                                                                                        highlightActiveLine={false}
                                                                                        setOptions={{ showLineNumbers: true, tabSize: 2, useWorker: false }}
                                                                                    />
                                                                                </Box>
                                                                                <Box style={{ flex: 1, overflow: 'hidden' }}>
                                                                                    <HtmlPreview content={typeof value === 'string' ? value : ''} emptyMessage="No content to preview" height="400px" />
                                                                                </Box>
                                                                            </Box>
                                                                        </Tabs.Panel>
                                                                    </Tabs>
                                                                </Paper>
                                                            ) : isBodyField ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    <AceEditor
                                                                        mode="html"
                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                        value={value || ''}
                                                                        readOnly={true}
                                                                        name={`html-viewer-${key}`}
                                                                        editorProps={{ $blockScrolling: true }}
                                                                        width="100%"
                                                                        height="400px"
                                                                        fontSize={14}
                                                                        showPrintMargin={false}
                                                                        showGutter={true}
                                                                        highlightActiveLine={false}
                                                                        setOptions={{
                                                                            showLineNumbers: true,
                                                                            tabSize: 2,
                                                                            useWorker: false
                                                                        }}
                                                                    />
                                                                </Paper>
                                                            ) : (
                                                                <div
                                                                    data-notes={stringifyDebugNotes({
                                                                        field: key,
                                                                        fieldLower: key?.toLowerCase(),
                                                                        entityType,
                                                                        entityTypeLower: entityType?.toLowerCase(),
                                                                        valueType: typeof value,
                                                                        hasValue: value !== undefined && value !== null,
                                                                        renderer: shouldRenderAsGuid(key, value) || (key && isARef(createEntityRef(key), 'Id') && typeof value === 'string' && isGuid(value)) ? 'GuidDisplay' : shouldRenderAsSid(key, value) ? 'SidDisplay' : 'Typography'
                                                                    })}
                                                                    data-field={key}
                                                                    data-entity-type={entityType}
                                                                    data-renderer={shouldRenderAsGuid(key, value) || (key && isARef(createEntityRef(key), 'Id') && typeof value === 'string' && isGuid(value)) ? 'GuidDisplay' : shouldRenderAsSid(key, value) ? 'SidDisplay' : 'Typography'}
                                                                >
                                                                    <Box>
                                                                        {(shouldRenderAsGuid(key, value) || (key && (isARef(createEntityRef(key), 'Id') || key.toLowerCase().endsWith('id')) && typeof value === 'string' && isGuid(value))) && !isJsonField ? (
                                                                            <GuidDisplay
                                                                                value={String(value)}
                                                                                variant="compact"
                                                                                showCopyButton={true}
                                                                                showExpandButton={key ? !isARef(createEntityRef(key), 'Id') : true}
                                                                            />
                                                                        ) : shouldRenderAsSid(key, value) ? (
                                                                            <SidDisplay
                                                                                value={String(value)}
                                                                                variant="compact"
                                                                                showCopyButton={true}
                                                                                showExpandButton={true}
                                                                            />
                                                                        ) : shouldRenderAsDateTime(value) ? (
                                                                            <Box>
                                                                                {renderFieldValue(value)}
                                                                            </Box>
                                                                        ) : keyNorm === 'targettype' && typeof value === 'string' ? (
                                                                            <TargetTypeLink targetType={value} />
                                                                        ) : keyNorm === 'targetid' && typeof value === 'string' && (entity?.TargetType ?? entity?.targetType) ? (
                                                                            <TargetIdLink
                                                                                targetId={value}
                                                                                targetType={String(entity?.TargetType ?? entity?.targetType ?? '')}
                                                                                canNavigateToType={isAdmin || readableEntityIdsLower.has(String(entity?.TargetType ?? entity?.targetType ?? '').toLowerCase())}
                                                                                targetDisplayName={entity?.TargetDisplayName ?? entity?.targetDisplayName ?? ''}
                                                                            />
                                                                        ) : keyNorm === 'policyruleid' && typeof value === 'string' ? (
                                                                            <PolicyRuleIdLink
                                                                                policyRuleId={value}
                                                                                canNavigate={isAdmin || readableEntityIdsLower.has('policyrule')}
                                                                                policyRuleDisplayName={entity?.PolicyRuleDisplayName ?? entity?.policyRuleDisplayName ?? ''}
                                                                            />
                                                                        ) : value === NON_INDEXED_PROPERTY_STUB ? (
                                                                            renderNonIndexedNotLoadedField(key, noLengthCountIds)
                                                                        ) : (
                                                                            <Text
                                                                                ff={(keyNorm === 'id' || keyNorm === 'sequence') ? 'monospace' : undefined}
                                                                                style={{ wordBreak: 'break-word' }}
                                                                            >
                                                                                {renderFieldValue(value)}
                                                                            </Text>
                                                                        )}
                                                                        {/* Show related entities for PossessiveTargetAttribute */}
                                                                        {(key && isARef(createEntityRef(key), 'PossessiveTargetAttribute')) && possessiveTargetAttributeInfo && (
                                                                            <>
                                                                                {possessiveTargetAttributeInfo.entities.length > 0 && (
                                                                                    <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                        Used by: {possessiveTargetAttributeInfo.entities.join(', ')}
                                                                                    </Text>
                                                                                )}
                                                                                {possessiveTargetAttributeInfo.isReferenceTo && possessiveTargetAttributeInfo.isReferenceTo.length > 0 && (
                                                                                    <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                        Refs: {possessiveTargetAttributeInfo.isReferenceTo.join(', ')}
                                                                                    </Text>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                </div>
                                                            )}
                                                        </Box>
                                                        {canFoldLargeLoadedNonIndexed && isLargeNonIndexedFolded && (
                                                            <Text size="sm" c="dimmed" fs="italic" py={4}>
                                                                Loaded value folded.
                                                            </Text>
                                                        )}
                                                    </Grid.Col>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}

                {/* References */}
                {sectionVisible(categorizedProps.references) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="refs">
                            <Accordion.Item value="refs">
                                <Accordion.Control>
                                    <Title order={5}>
                                        References ({categorizedProps.references.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="md">
                                            {categorizedProps.references.map(([attr, value]) => {
                                                const tier3ItemForRef = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
                                                const tAttr = tier3ItemForRef?.Attribute;
                                                const isLargeArrayRef = tAttr?.Array === true && tAttr?.IsReference === true && (
                                                    (typeof tAttr.Count === 'number' && tAttr.Count > ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD) ||
                                                    (typeof tAttr.Length === 'number' && tAttr.Length > EAGER_LOAD_NON_INDEXED_MAX_LENGTH)
                                                );
                                                const isEmptyArray = Array.isArray(value) && value.length === 0;
                                                const entityIdForViewer = (fullEntity || entity)?.Id ?? (fullEntity || entity)?.id;
                                                return (
                                                    <Grid.Col span={12} key={attr.id} {...filterColProps(attr, value)}>
                                                    {Array.isArray(value) && value.length > 10 ? (
                                                        // Use scrollable, searchable component for large reference lists
                                                        <ReferenceListViewer
                                                            references={value}
                                                            label={attr.displayName}
                                                            maxHeight={200}
                                                            searchThreshold={10}
                                                        />
                                                    ) : isLargeArrayRef && isEmptyArray && entityIdForViewer ? (
                                                        <Box>
                                                            <Group gap="xs" align="center" mb="xs">
                                                                {renderFieldLabelWithTier3Debug(attr)}
                                                            </Group>
                                                            <PaginatedEntityViewer
                                                                entityType={entityType}
                                                                entityId={String(entityIdForViewer)}
                                                                attributeId={attr.id}
                                                                attributeLabel={attr.displayName}
                                                                targetEntitlement={targetEntitlement ?? null}
                                                                entityService={entityService}
                                                                count={typeof tAttr?.Count === 'number' ? tAttr.Count : null}
                                                                length={typeof tAttr?.Length === 'number' ? tAttr.Length : null}
                                                            />
                                                        </Box>
                                                    ) : Array.isArray(value) ? (
                                                        // Small lists: show count and use compact display
                                                        <Box>
                                                            {renderFieldLabelWithTier3Debug(attr, { gutterBottom: true, suffix: value.length })}
                                                            <Group gap="sm" wrap="wrap">
                                                                {value.map((ref: any, index: number) => (
                                                                    <ScimReferenceDisplay
                                                                        key={index}
                                                                        reference={ref}
                                                                        variant="compact"
                                                                        showType={true}
                                                                        showLink={true}
                                                                    />
                                                                ))}
                                                            </Group>
                                                        </Box>
                                                    ) : (
                                                        // Single reference
                                                        <Box>
                                                            <Group gap="xs" align="center" mb="xs">
                                                                {renderFieldLabelWithTier3Debug(attr)}
                                                                {(() => {
                                                                    const bindingViolation = getBindingViolationForField(attr, value);
                                                                    return bindingViolation ? (
                                                                        <Tooltip label={bindingViolation}>
                                                                            <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                        </Tooltip>
                                                                    ) : null;
                                                                })()}
                                                            </Group>
                                                            {renderReferenceField(value)}
                                                            {/* Show related entities for PossessiveTargetAttribute */}
                                                            {isA(attr, 'PossessiveTargetAttribute') && possessiveTargetAttributeInfo && (
                                                                <>
                                                                    {possessiveTargetAttributeInfo.entities.length > 0 && (
                                                                        <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                            Used by: {possessiveTargetAttributeInfo.entities.join(', ')}
                                                                        </Text>
                                                                    )}
                                                                    {possessiveTargetAttributeInfo.isReferenceTo && possessiveTargetAttributeInfo.isReferenceTo.length > 0 && (
                                                                        <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                            Refs: {possessiveTargetAttributeInfo.isReferenceTo.join(', ')}
                                                                        </Text>
                                                                    )}
                                                                </>
                                                            )}
                                                            {isSetTransition(entityType) && isA(attr, 'ActionWorkflow') && actionWorkflowError && (
                                                                <DialogErrorAlert error={actionWorkflowError} />
                                                            )}
                                                            {isSetTransition(entityType) && isA(attr, 'ActionWorkflow') && actionWorkflowValidating && (
                                                                <Group gap={4} align="center" mt="xs">
                                                                    <Loader size={16} />
                                                                    <Text size="xs" c="dimmed">
                                                                        Validating workflow...
                                                                    </Text>
                                                                </Group>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Grid.Col>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}

                {/* Arrays */}
                {sectionVisible(categorizedProps.arrays) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="arrays">
                            <Accordion.Item value="arrays">
                                <Accordion.Control>
                                    <Title order={5}>
                                        Collections ({categorizedProps.arrays.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="md">
                                            {categorizedProps.arrays.map(([attr, value]) => {
                                                const key = attr.id;
                                                const arrayValue = Array.isArray(value) ? value : [];
                                                const arrayBindingViolation = getBindingViolationForField(attr, value);
                                                const tier3ItemForArray = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, attr.id);
                                                const tAttr = tier3ItemForArray?.Attribute;
                                                const isLargeArrayRef = tAttr?.Array === true && tAttr?.IsReference === true && (
                                                    (typeof tAttr.Count === 'number' && tAttr.Count > ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD) ||
                                                    (typeof tAttr.Length === 'number' && tAttr.Length > EAGER_LOAD_NON_INDEXED_MAX_LENGTH)
                                                );
                                                const isNotLoaded = arrayValue.length === 0;
                                                return (
                                                    <Grid.Col span={12} key={attr.id} {...filterColProps(attr, value)}>
                                                        <Group gap="xs" align="center" mb="xs">
                                                            {renderFieldLabelWithTier3Debug(attr)}
                                                            {arrayBindingViolation ? (
                                                                <Tooltip label={arrayBindingViolation}>
                                                                    <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                </Tooltip>
                                                            ) : null}
                                                        </Group>
                                                        {isLargeArrayRef && isNotLoaded && ((fullEntity || entity)?.Id ?? (fullEntity || entity)?.id) ? (
                                                            <PaginatedEntityViewer
                                                                entityType={entityType}
                                                                entityId={String((fullEntity || entity)?.Id ?? (fullEntity || entity)?.id ?? '')}
                                                                attributeId={attr.id}
                                                                attributeLabel={attr.displayName}
                                                                targetEntitlement={targetEntitlement ?? null}
                                                                entityService={entityService}
                                                                count={typeof tAttr?.Count === 'number' ? tAttr.Count : null}
                                                                length={typeof tAttr?.Length === 'number' ? tAttr.Length : null}
                                                            />
                                                        ) : isA(attr, 'emails') ? (
                                                            <Group gap="sm" wrap="wrap">
                                                                {arrayValue.map((email: any, index: number) => (
                                                                    <Badge
                                                                        key={index}
                                                                        size="sm"
                                                                        color={email.primary ? 'blue' : 'gray'}
                                                                        variant={email.primary ? 'filled' : 'light'}
                                                                    >
                                                                        {email.value}
                                                                    </Badge>
                                                                ))}
                                                            </Group>
                                                        ) : isA(attr, 'phoneNumbers') ? (
                                                            <Group gap="sm" wrap="wrap">
                                                                {arrayValue.map((phone: any, index: number) => (
                                                                    <Badge
                                                                        key={index}
                                                                        size="sm"
                                                                        color={phone.primary ? 'blue' : 'gray'}
                                                                        variant={phone.primary ? 'filled' : 'light'}
                                                                    >
                                                                        {phone.value}
                                                                    </Badge>
                                                                ))}
                                                            </Group>
                                                        ) : (
                                                            <Group gap="sm" wrap="wrap">
                                                                {arrayValue
                                                                    .filter((item: any) => {
                                                                        // Filter out null, undefined, and empty strings
                                                                        if (item === null || item === undefined || item === '') {
                                                                            return false;
                                                                        }
                                                                        // Filter out empty objects
                                                                        if (typeof item === 'object') {
                                                                            const hasDisplayValue = item.displayName || item.name || item.value || item.id;
                                                                            if (!hasDisplayValue && Object.keys(item).length === 0) {
                                                                                return false;
                                                                            }
                                                                        }
                                                                        return true;
                                                                    })
                                                                    .map((item: any, index: number) => {
                                                                        // Handle different types of array items
                                                                        if (typeof item === 'object' && item !== null) {
                                                                            // If it's an object with display properties, show those
                                                                            const displayValue = item.displayName || item.name || item.value || item.id || JSON.stringify(item);
                                                                            // Skip if displayValue would be empty or just "{}"
                                                                            if (!displayValue || displayValue === '{}' || displayValue.trim() === '') {
                                                                                return null;
                                                                            }
                                                                            return (
                                                                                <Badge
                                                                                    key={index}
                                                                                    size="sm"
                                                                                    variant="light"
                                                                                >
                                                                                    {displayValue}
                                                                                </Badge>
                                                                            );
                                                                        } else {
                                                                            // For primitive values, convert to string
                                                                            const stringValue = String(item);
                                                                            // Skip if empty string
                                                                            if (stringValue.trim() === '') {
                                                                                return null;
                                                                            }
                                                                            return (
                                                                                <Badge key={index} variant="light" size="sm">{stringValue}</Badge>
                                                                            );
                                                                        }
                                                                    })
                                                                    .filter((chip: any) => chip !== null) // Remove null chips
                                                                }
                                                            </Group>
                                                        )}
                                                    </Grid.Col>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}

                {/* Boolean Flags */}
                {sectionVisible(categorizedProps.booleans) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="flags">
                            <Accordion.Item value="flags">
                                <Accordion.Control>
                                    <Title order={5}>
                                        Flags ({categorizedProps.booleans.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="md">
                                            {categorizedProps.booleans.map(([attr, value]) => {
                                                const key = attr.id;
                                                const boolBindingViolation = getBindingViolationForField(attr, value);
                                                return (
                                                    <Grid.Col span={4} key={attr.id} {...filterColProps(attr, value)}>
                                                        <Group gap="xs" align="center">
                                                            {renderFieldLabelWithTier3Debug(attr)}
                                                            {boolBindingViolation ? (
                                                                <Tooltip label={boolBindingViolation}>
                                                                    <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                </Tooltip>
                                                            ) : null}
                                                        </Group>
                                                        <Badge
                                                            size="sm"
                                                            color={value ? 'green' : 'gray'}
                                                        >
                                                            {value ? 'Yes' : 'No'}
                                                        </Badge>
                                                    </Grid.Col>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}

                {/* Membership Information */}
                {sectionVisible(categorizedProps.memberships) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="memberships">
                            <Accordion.Item value="memberships">
                                <Accordion.Control>
                                    <Title order={5}>
                                        Group Memberships ({categorizedProps.memberships.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="lg">
                                            {categorizedProps.memberships.map(([attr, value]) => (
                                                <Grid.Col span={{ base: 12, md: 6 }} key={attr.id} {...filterColProps(attr, value)}>
                                                    <MembershipListViewer
                                                        memberships={value}
                                                        title={isA(attr, 'MemberOf') ? 'Direct Memberships' : 'Transitive Memberships'}
                                                        subtitle={isA(attr, 'MemberOf')
                                                            ? 'Groups this entity is directly a member of'
                                                            : 'Groups this entity is a member of through inheritance'
                                                        }
                                                        maxHeight={300}
                                                        showCount={true}
                                                        clickable={true}
                                                        enableSearch={true}
                                                        enableCopy={true}
                                                        enableDetails={true}
                                                    />
                                                </Grid.Col>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}

                {/* System Information */}
                {sectionVisible(categorizedProps.system) && (
                    <Grid.Col span={12}>
                        <Accordion defaultValue="system">
                            <Accordion.Item value="system">
                                <Accordion.Control>
                                    <Title order={5}>
                                        System Information ({categorizedProps.system.length})
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper p="md" withBorder>
                                        <Grid gutter="md">
                                            {categorizedProps.system.map(([attr, value]) => {
                                                const key = attr.id;
                                                const keyNorm = (key ?? '').trim().toLowerCase().replace(/^\$\./, '');
                                                const encSummarySystem = renderEncryptedSummaryField(attr, value);
                                                if (encSummarySystem) return encSummarySystem;

                                                // Use centralized field type detection
                                                const fieldInfo = detectFieldType(key, value, entityType, fullEntity, 'system');

                                                // Extract commonly used flags
                                                const {
                                                    isSqlField,
                                                    isWorkflowConfig: isConfigField,
                                                    isScriptField,
                                                    isJsonField: detectedIsJsonField,
                                                    isJsonStringField,
                                                    isJsonObject,
                                                    isOperationField,
                                                    isContentBodyMarkdown,
                                                    isContentBodyHtml,
                                                    isBodyField
                                                } = fieldInfo;

                                                // Force Modification field to render as JSON (Ace viewer), never as a GUID/reference widget.
                                                const isForcedModificationJsonField = isARef(createEntityRef(key ?? ''), 'modification');
                                                const isJsonField = detectedIsJsonField || isForcedModificationJsonField;

                                                // When SqlStatement/SqlStatementExplicit is empty or whitespace-only, show a note instead of the SQL viewer
                                                const isSqlValueEmptySystem = isSqlField && (value == null || (typeof value === 'string' && value.trim() === ''));
                                                const canFoldLargeLoadedNonIndexedSystem = isLargeLoadedNonIndexedField(key, value);
                                                const isLargeNonIndexedFoldedSystem = canFoldLargeLoadedNonIndexedSystem && isNonIndexedFieldFolded('system', key);

                                                // Debug logging for Operations field
                                                if (isOperationField || fieldInfo.isOperationsField) {
                                                    debugVerboseLog('[EntityDetailDialog] Operations field detected in system section:', {
                                                        key,
                                                        valueType: typeof value,
                                                        valueLength: typeof value === 'string' ? value.length : 'N/A',
                                                        valuePreview: typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100),
                                                        isJsonStringCheck: isJsonStringField,
                                                        isJsonStringField,
                                                        isJsonObject,
                                                        isJsonField,
                                                        isString: typeof value === 'string',
                                                        isObject: typeof value === 'object',
                                                        entityType
                                                    });
                                                }

                                                // Debug data-notes for Script field
                                                const scriptDebugNotes = isScriptField ? stringifyDebugNotes({
                                                    key,
                                                    keyLower: key?.toLowerCase(),
                                                    entityType,
                                                    entityTypeLower: entityType?.toLowerCase(),
                                                    isScriptField,
                                                    valueType: typeof value,
                                                    hasValue: value !== undefined && value !== null,
                                                    valueLength: typeof value === 'string' ? value.length : 'N/A'
                                                }) : '';

                                                return (
                                                    <Grid.Col
                                                        span={isSqlField || isConfigField || isJsonField || isScriptField || isBodyField || isContentBodyMarkdown || isContentBodyHtml ? 12 : 6}
                                                        key={attr.id}
                                                        {...filterColProps(attr, value)}
                                                        data-notes={isScriptField ? scriptDebugNotes : undefined}
                                                        data-field={key}
                                                        data-is-script-field={isScriptField}
                                                        data-is-config-field={isConfigField}
                                                        data-entity-type={entityType}
                                                    >
                                                        {(isConfigField || isJsonField) && typeof value === 'string' && value !== NON_INDEXED_PROPERTY_STUB && isJsonFieldReturnedAsString(value, key) && (
                                                            <Alert color="yellow" mb="xs">{JSON_FIELD_STRING_WARNING_MESSAGE}</Alert>
                                                        )}
                                                        <Group gap={4} align="center" mb="xs">
                                                            <span
                                                                data-notes={stringifyDebugNotes({
                                                                    field: key,
                                                                    fieldLower: key?.toLowerCase(),
                                                                    entityType,
                                                                    entityTypeLower: entityType?.toLowerCase(),
                                                                    isSqlField,
                                                                    isConfigField,
                                                                    isScriptField,
                                                                    isBodyField,
                                                                    valueType: typeof value,
                                                                    hasValue: value !== undefined && value !== null,
                                                                    valueLength: typeof value === 'string' ? value.length : Array.isArray(value) ? value.length : 'N/A',
                                                                    renderer: isSqlField ? 'AceEditor-sql' : isConfigField ? 'AceEditor-json' : isScriptField ? 'AceEditor-javascript' : isBodyField ? 'AceEditor-html' : value && typeof value === 'object' && (value.$ref || value.Id || value.DisplayName) ? 'ReferenceField' : 'Typography'
                                                                })}
                                                                data-field={key}
                                                                data-entity-type={entityType}
                                                                data-is-script-field={isScriptField}
                                                                data-is-config-field={isConfigField}
                                                                data-is-sql-field={isSqlField}
                                                                data-is-body-field={isBodyField}
                                                                data-renderer={isSqlField ? 'AceEditor-sql' : isConfigField ? 'AceEditor-json' : isScriptField ? 'AceEditor-javascript' : isBodyField || isContentBodyHtml ? 'AceEditor-html' : isContentBodyMarkdown ? 'MarkdownPreview' : value && typeof value === 'object' && (value.$ref || value.Id || value.DisplayName) ? 'ReferenceField' : 'Typography'}
                                                                style={{ display: 'inline-block' }}
                                                            >
                                                                {renderFieldLabelWithTier3Debug(attr)}
                                                                {(() => {
                                                                    const sysBindingViolation = getBindingViolationForField(attr, value);
                                                                    return sysBindingViolation ? (
                                                                        <Tooltip label={sysBindingViolation}>
                                                                            <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                        </Tooltip>
                                                                    ) : null;
                                                                })()}
                                                            </span>
                                                            {(isConfigField || isJsonField || isScriptField) && (
                                                                <>
                                                                    <Tooltip label={
                                                                        (isConfigField && configCopied) || (isScriptField && scriptCopied)
                                                                            ? "Copied!" : `Copy ${attr.displayName} to clipboard`
                                                                    }>
                                                                        <ActionIcon variant="subtle"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                const textToCopy = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                                                                                copyToClipboard(textToCopy);
                                                                                if (isConfigField) {
                                                                                    setConfigCopied(true);
                                                                                    setTimeout(() => setConfigCopied(false), 2000);
                                                                                } else if (isScriptField) {
                                                                                    setScriptCopied(true);
                                                                                    setTimeout(() => setScriptCopied(false), 2000);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <IconCopy size={16} color={
                                                                                (isConfigField && configCopied) || (isJsonField && configCopied) || (isScriptField && scriptCopied)
                                                                                    ? "var(--mantine-color-green-6)" : undefined
                                                                            } />
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                    <Tooltip label={
                                                                        (isConfigField && configExpanded !== 'collapsed') || (isJsonField && jsonExpanded !== 'collapsed') || (isScriptField && scriptExpanded !== 'collapsed')
                                                                            ? (
                                                                                isConfigField && configExpanded === 'expanded' ? 'Collapse to normal' :
                                                                                    isConfigField && configExpanded === 'normal' ? 'Minimize to single line' :
                                                                                        isJsonField && jsonExpanded === 'expanded' ? 'Collapse to normal' :
                                                                                            isJsonField && jsonExpanded === 'normal' ? 'Minimize to single line' :
                                                                                                isScriptField && scriptExpanded === 'expanded' ? 'Collapse to normal' :
                                                                                                    isScriptField && scriptExpanded === 'normal' ? 'Minimize to single line' :
                                                                                                        `Collapse ${key}`
                                                                            )
                                                                            : `Expand ${key} to full size`
                                                                    }>
                                                                        <ActionIcon variant="subtle"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                if (isConfigField) {
                                                                                    if (configExpanded === 'collapsed') {
                                                                                        setConfigExpanded('normal');
                                                                                    } else if (configExpanded === 'normal') {
                                                                                        setConfigExpanded('expanded');
                                                                                    } else {
                                                                                        setConfigExpanded('collapsed');
                                                                                    }
                                                                                } else if (isJsonField) {
                                                                                    if (jsonExpanded === 'collapsed') {
                                                                                        setJsonExpanded('normal');
                                                                                    } else if (jsonExpanded === 'normal') {
                                                                                        setJsonExpanded('expanded');
                                                                                    } else {
                                                                                        setJsonExpanded('collapsed');
                                                                                    }
                                                                                } else if (isScriptField) {
                                                                                    if (scriptExpanded === 'collapsed') {
                                                                                        setScriptExpanded('normal');
                                                                                    } else if (scriptExpanded === 'normal') {
                                                                                        setScriptExpanded('expanded');
                                                                                    } else {
                                                                                        setScriptExpanded('collapsed');
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            {(isConfigField && configExpanded !== 'collapsed') || (isJsonField && jsonExpanded !== 'collapsed') || (isScriptField && scriptExpanded !== 'collapsed') ? (
                                                                                <IconArrowsMinimize size={16} />
                                                                            ) : (
                                                                                <IconArrowsMaximize size={16} />
                                                                            )}
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                            {canFoldLargeLoadedNonIndexedSystem && (
                                                                <Tooltip label={isLargeNonIndexedFoldedSystem ? 'Unfold' : 'Fold'}>
                                                                    <ActionIcon variant="subtle"
                                                                        size="sm"
                                                                        onClick={() => toggleNonIndexedFieldFold('system', key)}
                                                                        aria-label={isLargeNonIndexedFoldedSystem ? 'Unfold' : 'Fold'}
                                                                    >
                                                                        {isLargeNonIndexedFoldedSystem ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </Group>
                                                        <Box
                                                            py="xs"
                                                            style={{ display: canFoldLargeLoadedNonIndexedSystem && isLargeNonIndexedFoldedSystem ? 'none' : 'block' }}
                                                            data-notes={stringifyDebugNotes({
                                                                field: key,
                                                                fieldLower: key?.toLowerCase(),
                                                                entityType,
                                                                entityTypeLower: entityType?.toLowerCase(),
                                                                isSqlField,
                                                                isConfigField,
                                                                isScriptField,
                                                                isBodyField,
                                                                valueType: typeof value,
                                                                hasValue: value !== undefined && value !== null
                                                            })}
                                                            data-field={key}
                                                            data-entity-type={entityType}
                                                        >
                                                            {isSqlField ? (
                                                                <SqlDisplayComponent value={value} displayName={attr.displayName} editorId={key} />
                                                            ) : isConfigField ? (
                                                                <Box
                                                                    data-notes={stringifyDebugNotes({ renderer: 'AceEditor', mode: 'json', field: key, entityType })}
                                                                    data-renderer="AceEditor"
                                                                    data-mode="json"
                                                                >
                                                                    <AceEditor
                                                                        mode="json"
                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                        value={value ? formatWorkflowConfigForDisplay(value) : '{}'}
                                                                        readOnly={true}
                                                                        name="config-viewer"
                                                                        editorProps={{ $blockScrolling: true }}
                                                                        width="100%"
                                                                        height={configExpanded === 'expanded' ? '600px' : '400px'}
                                                                        fontSize={14}
                                                                        showPrintMargin={false}
                                                                        showGutter={true}
                                                                        highlightActiveLine={false}
                                                                        setOptions={{
                                                                            showLineNumbers: true,
                                                                            tabSize: 2,
                                                                            useWorker: false,
                                                                            foldStyle: 'markbeginend',
                                                                            showFoldWidgets: true
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ) : isJsonField ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    {/* Debug logging for Operations field rendering in system section */}
                                                                    {(() => {
                                                                        if (key && (isARef(createEntityRef(key), 'Operations') || isARef(createEntityRef(key), 'Operation'))) {
                                                                            debugVerboseLog('[EntityDetailDialog] Rendering Operations as JSON in system section:', {
                                                                                key,
                                                                                isJsonStringField,
                                                                                isJsonObject,
                                                                                isJsonField,
                                                                                jsonExpanded,
                                                                                valueType: typeof value,
                                                                                valuePreview: typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100)
                                                                            });
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                    {jsonExpanded === 'collapsed' ? (
                                                                        <Box
                                                                            h={24}
                                                                            px="xs"
                                                                            py="xs"
                                                                            bg="var(--mantine-color-default-hover)"
                                                                            c="dimmed"
                                                                            ff="monospace"
                                                                            fz="xs"
                                                                            style={{
                                                                                overflow: 'hidden',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}
                                                                        >
                                                                            <Text
                                                                                size="xs"
                                                                                ff="monospace"
                                                                                fz="xs"
                                                                                style={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                {typeof value === 'string' ? value.substring(0, 100) : JSON.stringify(value).substring(0, 100)}...
                                                                            </Text>
                                                                        </Box>
                                                                    ) : (
                                                                        <AceEditor
                                                                            mode="json"
                                                                            theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                            value={typeof value === 'string' ? (() => {
                                                                                try {
                                                                                    return JSON.stringify(JSON.parse(value), null, 2);
                                                                                } catch {
                                                                                    return value;
                                                                                }
                                                                            })() : JSON.stringify(value, null, 2)}
                                                                            readOnly={true}
                                                                            name={`json-viewer-${key}`}
                                                                            editorProps={{ $blockScrolling: true }}
                                                                            width="100%"
                                                                            height={jsonExpanded === 'expanded' ? '600px' : '400px'}
                                                                            fontSize={14}
                                                                            showPrintMargin={false}
                                                                            showGutter={true}
                                                                            highlightActiveLine={false}
                                                                            setOptions={{
                                                                                showLineNumbers: true,
                                                                                tabSize: 2,
                                                                                useWorker: false,
                                                                                foldStyle: 'markbeginend',
                                                                                showFoldWidgets: true
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Paper>
                                                            ) : isScriptField ? (
                                                                <Paper
                                                                    withBorder
                                                                    mt="xs"
                                                                    bg="var(--mantine-color-default)"
                                                                    data-notes={scriptDebugNotes}
                                                                    data-field="Script"
                                                                    data-renderer="AceEditor"
                                                                    data-mode="javascript"
                                                                >
                                                                    <AceEditor
                                                                        mode="javascript"
                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                        value={String(value || '')}
                                                                        readOnly={true}
                                                                        name={`script-viewer-${key}`}
                                                                        editorProps={{ $blockScrolling: true }}
                                                                        width="100%"
                                                                        height={scriptExpanded ? '600px' : '400px'}
                                                                        fontSize={14}
                                                                        showPrintMargin={false}
                                                                        showGutter={true}
                                                                        highlightActiveLine={false}
                                                                        setOptions={{
                                                                            showLineNumbers: true,
                                                                            tabSize: 2,
                                                                            useWorker: false,
                                                                            showFoldWidgets: true
                                                                        }}
                                                                    />
                                                                </Paper>
                                                            ) : isContentBodyMarkdown ? (
                                                                <Box mt="xs">
                                                                    <ShadcnContentViewer content={value || ''} emptyMessage="No content to display" height="400px" />
                                                                </Box>
                                                            ) : isContentBodyHtml ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    <Tabs
                                                                        value={String(Math.min(htmlBodyTabByField[`system:${key}`] ?? 0, 2))}
                                                                        onChange={(v) => v != null && setHtmlBodyTabByField((prev) => ({ ...prev, [`system:${key}`]: Number(v) }))}
                                                                    >
                                                                        <Tabs.List>
                                                                            <Tabs.Tab value="0">Source</Tabs.Tab>
                                                                            <Tabs.Tab value="1">Preview</Tabs.Tab>
                                                                            <Tabs.Tab value="2">Side by side</Tabs.Tab>
                                                                        </Tabs.List>
                                                                        <Tabs.Panel value="0" pt="xs">
                                                                            <AceEditor
                                                                                mode="html"
                                                                                theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                                value={value || ''}
                                                                                readOnly={true}
                                                                                name={`html-viewer-system-${key}`}
                                                                                editorProps={{ $blockScrolling: true }}
                                                                                width="100%"
                                                                                height="400px"
                                                                                fontSize={14}
                                                                                showPrintMargin={false}
                                                                                showGutter={true}
                                                                                highlightActiveLine={false}
                                                                                setOptions={{
                                                                                    showLineNumbers: true,
                                                                                    tabSize: 2,
                                                                                    useWorker: false
                                                                                }}
                                                                            />
                                                                        </Tabs.Panel>
                                                                        <Tabs.Panel value="1" pt="xs">
                                                                            <HtmlPreview content={typeof value === 'string' ? value : ''} emptyMessage="No content to preview" height="400px" />
                                                                        </Tabs.Panel>
                                                                        <Tabs.Panel value="2" pt="xs">
                                                                            <Box style={{ display: 'flex', height: '400px' }}>
                                                                                <Box style={{ flex: 1, borderRight: 1, borderColor: 'var(--mantine-color-default-border)' }}>
                                                                                    <AceEditor
                                                                                        mode="html"
                                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                                        value={value || ''}
                                                                                        readOnly={true}
                                                                                        name={`html-viewer-system-side-${key}`}
                                                                                        editorProps={{ $blockScrolling: true }}
                                                                                        width="100%"
                                                                                        height="400px"
                                                                                        fontSize={14}
                                                                                        showPrintMargin={false}
                                                                                        showGutter={true}
                                                                                        highlightActiveLine={false}
                                                                                        setOptions={{ showLineNumbers: true, tabSize: 2, useWorker: false }}
                                                                                    />
                                                                                </Box>
                                                                                <Box style={{ flex: 1, overflow: 'hidden' }}>
                                                                                    <HtmlPreview content={typeof value === 'string' ? value : ''} emptyMessage="No content to preview" height="400px" />
                                                                                </Box>
                                                                            </Box>
                                                                        </Tabs.Panel>
                                                                    </Tabs>
                                                                </Paper>
                                                            ) : isBodyField ? (
                                                                <Paper withBorder mt="xs" bg="var(--mantine-color-default)">
                                                                    <AceEditor
                                                                        mode="html"
                                                                        theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                        value={value || ''}
                                                                        readOnly={true}
                                                                        name={`html-viewer-${key}`}
                                                                        editorProps={{ $blockScrolling: true }}
                                                                        width="100%"
                                                                        height="400px"
                                                                        fontSize={14}
                                                                        showPrintMargin={false}
                                                                        showGutter={true}
                                                                        highlightActiveLine={false}
                                                                        setOptions={{
                                                                            showLineNumbers: true,
                                                                            tabSize: 2,
                                                                            useWorker: false
                                                                        }}
                                                                    />
                                                                </Paper>
                                                            ) : shouldRenderAsGuid(key, value) ? (
                                                                <GuidDisplay
                                                                    value={String(value)}
                                                                    variant="compact"
                                                                    showCopyButton={true}
                                                                    showExpandButton={true}
                                                                />
                                                            ) : shouldRenderAsSid(key, value) ? (
                                                                <SidDisplay
                                                                    value={String(value)}
                                                                    variant="compact"
                                                                    showCopyButton={true}
                                                                    showExpandButton={true}
                                                                />
                                                            ) : shouldRenderAsDateTime(value) ? (
                                                                <Box>
                                                                    <Text>
                                                                        {formatDateTime(value)}
                                                                    </Text>
                                                                </Box>
                                                            ) : keyNorm === 'targettype' && typeof value === 'string' ? (
                                                                <TargetTypeLink targetType={value} />
                                                            ) : keyNorm === 'targetid' && typeof value === 'string' && (entity?.TargetType ?? entity?.targetType) ? (
                                                                <TargetIdLink
                                                                    targetId={value}
                                                                    targetType={String(entity?.TargetType ?? entity?.targetType ?? '')}
                                                                    canNavigateToType={isAdmin || readableEntityIdsLower.has(String(entity?.TargetType ?? entity?.targetType ?? '').toLowerCase())}
                                                                    targetDisplayName={entity?.TargetDisplayName ?? entity?.targetDisplayName ?? ''}
                                                                />
                                                            ) : keyNorm === 'policyruleid' && typeof value === 'string' ? (
                                                                <PolicyRuleIdLink
                                                                    policyRuleId={value}
                                                                    canNavigate={isAdmin || readableEntityIdsLower.has('policyrule')}
                                                                    policyRuleDisplayName={entity?.PolicyRuleDisplayName ?? entity?.policyRuleDisplayName ?? ''}
                                                                />
                                                            ) : value && typeof value === 'object' && (value.$ref || value.Id || value.DisplayName) && !isForcedModificationJsonField && !isJsonField ? (
                                                                <Box
                                                                    data-notes={stringifyDebugNotes({
                                                                        field: key,
                                                                        renderer: 'ReferenceField',
                                                                        valueType: typeof value,
                                                                        hasRef: !!value.$ref,
                                                                        hasId: !!value.Id,
                                                                        hasDisplayName: !!value.DisplayName
                                                                    })}
                                                                    data-field={key}
                                                                    data-renderer="ReferenceField"
                                                                >
                                                                    {renderReferenceField(value)}
                                                                    {/* Show related entities for PossessiveTargetAttribute */}
                                                                    {(key && isARef(createEntityRef(key), 'PossessiveTargetAttribute')) && possessiveTargetAttributeInfo && (
                                                                        <>
                                                                            {possessiveTargetAttributeInfo.entities.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Used by: {possessiveTargetAttributeInfo.entities.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                            {possessiveTargetAttributeInfo.isReferenceTo && possessiveTargetAttributeInfo.isReferenceTo.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Refs: {possessiveTargetAttributeInfo.isReferenceTo.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Box
                                                                    data-notes={stringifyDebugNotes({
                                                                        field: key,
                                                                        renderer: shouldRenderAsGuid(key, value) ? 'GuidDisplay' : shouldRenderAsSid(key, value) ? 'SidDisplay' : 'Typography',
                                                                        valueType: typeof value,
                                                                        isString: typeof value === 'string',
                                                                        isNumber: typeof value === 'number',
                                                                        isBoolean: typeof value === 'boolean',
                                                                        isArray: Array.isArray(value),
                                                                        isObject: typeof value === 'object' && value !== null,
                                                                        valueLength: typeof value === 'string' ? value.length : Array.isArray(value) ? value.length : 'N/A'
                                                                    })}
                                                                    data-field={key}
                                                                    data-renderer={shouldRenderAsGuid(key, value) ? 'GuidDisplay' : shouldRenderAsSid(key, value) ? 'SidDisplay' : 'Typography'}
                                                                >
                                                                    {shouldRenderAsGuid(key, value) ? (
                                                                        <GuidDisplay
                                                                            value={String(value)}
                                                                            variant="compact"
                                                                            showCopyButton={true}
                                                                            showExpandButton={key !== 'Id' && key !== 'id'}
                                                                        />
                                                                    ) : shouldRenderAsSid(key, value) ? (
                                                                        <SidDisplay
                                                                            value={String(value)}
                                                                            variant="compact"
                                                                            showCopyButton={true}
                                                                            showExpandButton={true}
                                                                        />
                                                                    ) : shouldRenderAsDateTime(value) ? (
                                                                        <Box
                                                                            data-notes={stringifyDebugNotes({ field: key, valueType: typeof value })}
                                                                            data-field={key}
                                                                        >
                                                                            {renderFieldValue(value)}
                                                                        </Box>
                                                                    ) : value === NON_INDEXED_PROPERTY_STUB ? (
                                                                        renderNonIndexedNotLoadedField(key, noLengthCountIds)
                                                                    ) : (
                                                                        <Text
                                                                            ff={key && isARef(createEntityRef(key), 'Id') ? 'monospace' : undefined}
                                                                            data-notes={stringifyDebugNotes({ field: key, valueType: typeof value })}
                                                                            data-field={key}
                                                                        >
                                                                            {renderFieldValue(value)}
                                                                        </Text>
                                                                    )}
                                                                    {/* Show related entities for PossessiveTargetAttribute */}
                                                                    {(key && isARef(createEntityRef(key), 'PossessiveTargetAttribute')) && possessiveTargetAttributeInfo && (
                                                                        <>
                                                                            {possessiveTargetAttributeInfo.entities.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Used by: {possessiveTargetAttributeInfo.entities.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                            {possessiveTargetAttributeInfo.isReferenceTo && possessiveTargetAttributeInfo.isReferenceTo.length > 0 && (
                                                                                <Text size="xs" c="dimmed" display="block" mt={4}>
                                                                                    Refs: {possessiveTargetAttributeInfo.isReferenceTo.join(', ')}
                                                                                </Text>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        {canFoldLargeLoadedNonIndexedSystem && isLargeNonIndexedFoldedSystem && (
                                                            <Text size="sm" c="dimmed" fs="italic" py={4}>
                                                                Loaded value folded.
                                                            </Text>
                                                        )}
                                                    </Grid.Col>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>
                )}
            </>
        );
    };

    return renderEntityProperties(entityProp);
};

